import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit, Inject, PLATFORM_ID, ChangeDetectorRef } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil, take, firstValueFrom } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { WebsiteGenerationService } from '../../../services/website-generation.service';
import { environment } from '../../../../environments/environment';
import { DeploymentStatusTypes } from '../../../utils/status-messages';
import { DeploymentProgressMessages, DeploymentSuccessMessages } from '../../../utils/deployment-messages';
import { FormBuilderV2Service, SectionCompletionStates } from '../../../services/v2/form-builder-v2.service';
import { CompanyIdService } from '../../../services/company-id.service';
import { PreviewV2Service } from '../../../services/v2/preview-v2.service';
import { AIContentService } from '../../../services/ai-content.service';
import { WebsiteFileService } from '../../../services/website-file.service';
import { COLOR_THEMES } from '../../../models/color-theme.model';
import { FormBuilderPreviewComponent } from './form-builder-preview.component';
import { ContentEditRequest } from '../../preview/inline-content-editor/inline-content-editor.component';
import { ImageUploadRequest } from '../../preview/inline-image-uploader/inline-image-uploader.component';
import { InlineContentEditorV2Component, ContentEditRequestV2 } from './inline-content-editor-v2.component';
import { InlineImageUploaderV2Component, ImageUploadRequestV2 } from './inline-image-uploader-v2.component';
import { IndexedDBService } from '../../../services/indexeddb.service';
import { PhoneFormatterUtil } from '../../../utils/phone-formatter.util';
import { SupplierData } from '../../../interfaces/supplier-data.interface';
import { SitePublishingComponent } from '../site-publishing/site-publishing.component';
import { WebsiteProgressService } from '../../../services/website-progress.service';
import { WarningPopupComponent } from '../../shared/warning-popup/warning-popup.component';
import { MixPanelService } from '../../../services/v2/mixpanel.service';
import { SupplierSiteDataService } from '../../../services/v2/supplier-site-data.service';
import { TutorialPopupComponent, TutorialConfig } from '../../shared/tutorial-popup/tutorial-popup.component';
import { TutorialConfigService } from '../../../services/tutorial-config.service';
import { ImageUploadService } from '../../../services/image-upload.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-form-builder-v2',
  standalone: true,
  imports: [CommonModule, FormsModule, HttpClientModule, FormBuilderPreviewComponent, InlineContentEditorV2Component, InlineImageUploaderV2Component, SitePublishingComponent, WarningPopupComponent, TutorialPopupComponent],
  templateUrl: './form-builder.component.html',
  styleUrls: ['./form-builder.component.scss']
})
export class FormBuilderV2Component implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('websitePreview') websitePreviewComponent!: FormBuilderPreviewComponent;
  @ViewChild('colorInput') colorInput!: ElementRef<HTMLInputElement>;

  private destroy$ = new Subject<void>();
  public companyGuid: string | null = null;
  private supplierId: string | null = null;
  editMode: string | null = null;
  private companyNameFromMetadata: string = ''; // Store company name from metadata
  

  // Form data struct
  formData = {
    home: {
      heroTitle: '',
      heroSubtitle: '',
      heroDescription: ''
    },
    template: 1,
    theme: 'default',
    customThemeColor: '#2e58d6',
    capabilities: [] as string[],
    additionalCapabilities: '',
    equipment: '',
    aboutUs: '',
    contact: {
      email: '',
      phone: '',
      streetAddress: '',
      city: '',
      state: '',
      zipCode: ''
    },
    logo: null as File | null,
    images: {
      aboutUs: [] as File[],
      capabilities: [] as File[]
    },
    deploymentId: null as string | null,
    // Track uploaded images per section for validation (persisted automatically)
    uploadedImagesBySection: {}
  };

  // Section completion tracking
  isHomeCompleted = false;
  isThemeCompleted = false;
  isCapabilitiesCompleted = false;
  isAboutCompleted = false;
  isContactCompleted = false;
  isLogoCompleted = false;

  // Content generation tracking
  isAboutGenerated = false;
  isCapabilitiesGenerated = false;

  isThemeCompleting = false;
  isAboutCompleting = false;
  isCapabilitiesCompleting = false;
  isContactCompleting = false;
  isLogoCompleting = false;

  

  // Current active section for preview synchronization
  currentActiveSection: string | null = null;

  // Component state
  currentStep: 'template' | 'capabilities' | 'about-us' | 'contact' | 'logo' = 'template';
  isLoading = false;
  completionPercentage: number = 0;

  // Section completion tracking (not used in new flow)
  sectionCompletion = {
    template: false,
    capabilities: false,
    aboutUs: false,
    contact: false,
    logo: false
  };

  // Template and theme options
  templates = [
    { id: 1, name: 'Industrial', image: 'assets/templates/supplier-template-1/index.html' },
    { id: 2, name: 'Bold', image: 'assets/templates/supplier-template-2/index.html' },
    { id: 3, name: 'Modern', image: 'assets/templates/supplier-template-3/index.html' },
    { id: 4, name: 'Rugged', image: 'assets/templates/supplier-template-4/index.html' },
    { id: 5, name: 'Purposeful', image: 'assets/templates/supplier-template-5/index.html' },
  ];

  // Template-specific default colors
  templateDefaultColors: { [templateId: number]: string } = {
    1: '#0078D4', // Industrial - Blue
    2: '#D84324', // Bold - Blue
    3: '#0F73F6', // Modern - Blue
    4: '#1F3D1B', // Rugged - Green
    5: '#50C878'  // Purposeful - Green
  };

  // Use existing color themes from V1 system
  colors = Object.entries(COLOR_THEMES).map(([key, theme]) => ({
    key: key,
    name: theme.name,
    theme: theme
  }));

  // Get available colors including default template color
  getAvailableColors() {
    const availableColors = [...this.colors];
    
    // Add default template color if a template is selected
    if (this.formData.template && this.templateDefaultColors[this.formData.template]) {
      const defaultColor = this.templateDefaultColors[this.formData.template];
      const templateName = this.templates.find(t => t.id === this.formData.template)?.name || 'Default';
      
      // Check if default color already exists in the colors array
      // const existingColor = availableColors.find(color => color.theme.primary === defaultColor);
      
      // if (!existingColor) {
        availableColors.unshift({
          key: 'default',
          name: `${templateName} Default`,
          theme: {
            name: `${templateName} Default`,
            primary: defaultColor,
            background: defaultColor
          }
        });
      // }
    }
    
    return availableColors;
  }

  // Get default color for a specific template
  getDefaultColorForTemplate(templateId: number): string | null {
    return this.templateDefaultColors[templateId] || null;
  }

  // Get template name for a specific template ID
  getTemplateName(templateId: number): string {
    const template = this.templates.find(t => t.id === templateId);
    return template ? template.name : 'Default';
  }

  capabilitiesOptions = ['3D Printing','Blow Molding','Compression Molding','Die Casting','Electro-Mechanical Assembly','Engineering & Design Services','Extrusions','Fabrication','Forging','Gears','Injection Molding','Investment Casting','Machining','Printed Circuit Boards','Rotational Molding','RTV Molding','Sand Mold Casting','Sheet Metal','Springs & Wire Forming','Stamping','Structural Foam Molding','Thermoforming','Tool, Die & Mold Making','Tube Modification','Wire Harness','Woodworking'];

  // New state for content generation
  canGenerateContent = false;
  isGeneratingContent = false;
  isCapabilitiesDropdownOpen = false;
  hasCapabilitiesSelectionChangedSinceGeneration = false;
  hasGenerateCapabilitiesBeenClickedSinceSelectionChange = false;
  private lastGeneratedCapabilities: string[] = [];
  private shouldCompleteCapabilitiesAfterGeneration = false;
  
  // About Us content generation state
  canGenerateAboutUsContent = false;
  isGeneratingAboutUsContent = false;

  // Flag to track if AI content has been generated (for edit mode control)
  // AI content generation state is now managed by FormBuilderV2Service

  // V2 Inline Editor State Management
  isEditingContent: { [section: string]: boolean } = {};
  currentContentEditRequest: { [section: string]: ContentEditRequestV2 | null } = {};
  
  // Image Upload State Management
  isUploadingImage: { [tagName: string]: boolean } = {};
  
  // Custom Color Picker State
  showCustomColorPicker: boolean = false;
  customThemeColor: string = '#2e58d6';
  
  // Preview Loading State
  isPreviewLoading: boolean = false;
  
  // Deployment State
  isDeploying: boolean = false;
  deploymentStatus: string = '';
  deploymentProgress: number = 0;
  deploymentAttempts: number = 0;
  potentialWebsiteUrl: string = '';
  deploymentFinalMessage: string = '';
  deploymentWebsiteUrl: string = '';
  showDeploymentFinalMessage: boolean = false;
  deploymentIconStatus: 'loading' | 'success' | 'error' = 'loading';
  
  // Save for Later State
  isSavingForLater: boolean = false;
  
  // Publish Button Loading State
  isPublishButtonLoading: boolean = false;
  
  // Warning popup properties
  showWarningPopup = false;
  warningMessage = '';
  dashboardUrl = '/';

  // Image warning overlay properties
  showImageWarningOverlay = true; // Set to true to show on page load
  imageWarningMessage = 'Image warning message will be updated later';

  // Tutorial popup properties
  showTutorialPopup = false;
  tutorialConfig: TutorialConfig | null = null;

  // Contact form validation
  contactFieldErrors: { [key: string]: boolean } = {};


  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private formBuilderV2Service: FormBuilderV2Service,
    private companyIdService: CompanyIdService,
    private previewService: PreviewV2Service,
    private aiContentService: AIContentService,
    private websiteFileService: WebsiteFileService,
    private indexedDBService: IndexedDBService,
    private websiteGenerationService: WebsiteGenerationService,
    private websiteProgressService: WebsiteProgressService,
    private mixPanelService: MixPanelService,
    private supplierSiteDataService: SupplierSiteDataService,
    private tutorialConfigService: TutorialConfigService,
    private imageUploadService: ImageUploadService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private toastr: ToastrService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  async ngOnInit(): Promise<void> {
         
    // Only run browser-specific code if we're in a browser environment
    if (isPlatformBrowser(this.platformId)) {
      
      
      await this.initializeForm();
      
      
      // Initialize uploadedImagesBySection if not already set
      if (!this.formData.uploadedImagesBySection) {
        this.formData.uploadedImagesBySection = {
          'about-us': [],
          'capabilities': [],
          'logo': []
        };
      }
      if (this.editMode) {
        this.onBackToEditMode();
      } 
      
      this.addClickOutsideHandler();
      // Initialize MixPanel
      this.mixPanelService.initialize();
      if(this.supplierId != null){
        this.mixPanelService.identify(this.supplierId);
      }
      // Load persisted section state after form data is loaded
      // This ensures the section state is restored from the same IndexedDB data
      await this.loadCurrentSectionState();
      
      // Load section completion states from IndexedDB
      await this.loadSectionCompletionStates();
      
      // Smart detection of completion states based on existing data
      // await this.detectCompletionStatesFromData();
      
      // Set initial preview to the loaded section
      this.updatePreviewForSection(this.currentActiveSection || 'template');
      this.refreshImageTagsCache();
      
      // Update initial website progress
      // this.updateWebsiteProgress('template');
      
      // Check if AI content has been generated and set the flag
      // AI content generation state is now managed by FormBuilderV2Service
      
      // Update edit mode based on current section
      this.updateEditModeForCurrentSection();

      if(this.formData.deploymentId && this.editMode === null) {
        this.previewService.setEditMode(false, this.companyGuid || undefined);
        this.previewService.loadTemplatePage('index', this.getCurrentSupplierData(), this.companyGuid || undefined);
        this.pollDeploymentStatus(this.formData.deploymentId);
      }
    }
  }

  ngAfterViewInit(): void {
    // Only run browser-specific code if we're in a browser environment
    if (isPlatformBrowser(this.platformId)) {
      // Initialize any DOM-dependent functionality
      this.cdr.detectChanges();
      this.initializeAccordion();
      
      // Add click outside handler for dropdown
      this.addClickOutsideHandler();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    // Only run browser-specific code if we're in a browser environment
    if (isPlatformBrowser(this.platformId)) {
      // Remove click outside handler
      this.removeClickOutsideHandler();
    }
  }

  // Multiselect dropdown methods
  toggleCapabilitiesDropdown(): void {
    this.isCapabilitiesDropdownOpen = !this.isCapabilitiesDropdownOpen;
  }

  isCapabilitySelected(capability: string): boolean {
    return this.formData.capabilities.includes(capability);
  }

  private areCapabilitiesEqual(first: string[], second: string[]): boolean {
    if (first.length !== second.length) {
      return false;
    }

    const firstSorted = [...first].sort();
    const secondSorted = [...second].sort();
    return firstSorted.every((capability, index) => capability === secondSorted[index]);
  }

  private markCapabilitiesSelectionChangedIfNeeded(): void {
    const hasChanged = !this.areCapabilitiesEqual(this.formData.capabilities, this.lastGeneratedCapabilities);
    this.hasCapabilitiesSelectionChangedSinceGeneration = hasChanged;

    if (hasChanged) {
      this.hasGenerateCapabilitiesBeenClickedSinceSelectionChange = false;
    }
  }

  private markCapabilitiesGenerationAsUpToDate(): void {
    this.lastGeneratedCapabilities = [...this.formData.capabilities];
    this.hasCapabilitiesSelectionChangedSinceGeneration = false;
    this.hasGenerateCapabilitiesBeenClickedSinceSelectionChange = true;
  }

  toggleCapability(capability: string): void {
    const index = this.formData.capabilities.indexOf(capability);
    
    if (index === -1) {
      // Add capability
      this.formData.capabilities.push(capability);
    } else {
      // Remove capability
      this.formData.capabilities.splice(index, 1);
    }
    
    console.log('Capabilities updated:', this.formData.capabilities);
    
    // Update service
    this.formBuilderV2Service.updateCapabilities(this.formData.capabilities);
    
    // Check if generate buttons should be enabled
    this.checkGenerateButtonState();
    this.markCapabilitiesSelectionChangedIfNeeded();
    
    // Ensure preview stays on capabilities page during multiselect operations
    if (this.currentActiveSection === 'capabilities') {
      this.updatePreviewForSection('capabilities');
    }
  }

  /**
   * Restore saved data from backend API and initialize IndexedDB
   */
  private async restoreSavedDataFromBackend(): Promise<void> {
    try {
      
      const response = await this.supplierSiteDataService.getSupplierSiteData(this.companyGuid!);
      
      if (response.status && response.data?.success && response.data?.data) {
        
        const savedData = response.data.data;
        
        // Parse and restore dictContent
        if (savedData.dictContent) {
          try {
            const dictContent = JSON.parse(savedData.dictContent);
            // Check if dictContent is not an empty object
            if (Object.keys(dictContent).length > 0) {
              await this.indexedDBService.storeDictContent(this.companyGuid!, dictContent);
            }
          } catch (parseError) {
            console.error('❌ Failed to parse dictContent:', parseError);
          }
        }
        
        // Parse and restore completeFormData
        if (savedData.completeFormData && Object.keys(savedData.completeFormData).length > 0) {
          try {
            const completeFormData = JSON.parse(savedData.completeFormData);
            await this.indexedDBService.storeMetadata(`v2_completeFormData_${this.companyGuid}`, completeFormData);
            console.log('✅ Restored completeFormData to IndexedDB');
            
            // Update form data in the service
            this.formBuilderV2Service.updateFormData(completeFormData);
            
            // ✅ CRITICAL FIX: Restore completion percentage from backend data
            if (completeFormData.completionPercentage !== undefined) {
              this.completionPercentage = completeFormData.completionPercentage;
              console.log('✅ Restored completion percentage from backend:', this.completionPercentage);
            }
          } catch (parseError) {
            console.error('❌ Failed to parse completeFormData:', parseError);
          }
        }
        
        // Restore tagReplacements
        if (savedData.tagReplacements && Array.isArray(savedData.tagReplacements)) {
          await this.indexedDBService.storeTagReplacements(this.companyGuid!, savedData.tagReplacements);
          console.log('✅ Restored tagReplacements to IndexedDB');
        }
        
        
        // Update the preview with restored data
        await this.previewService.restoreDictContentFromIndexedDB(this.companyGuid!);
        await this.previewService.restoreTagReplacementsFromIndexedDB(this.companyGuid!);
        
      }
      
    } catch (error) {
      console.error('❌ Failed to restore saved data from backend:', error);
      // Don't throw error - this is not critical for form initialization
    }
  }

  private async initializeForm(): Promise<void> {
    
    try {
      // Get query parameters synchronously first
      const params = await firstValueFrom(this.route.queryParams);
      
      const encodedId = params?.['cid'] || null;
      const encodedGuid = params?.['guid'] || null;
      const encodedSupplierId = params?.['sid'] || null;
      const editMode = params?.['ed'] || null;
      
      // if (!encodedId) {
      //   console.log('FormBuilderV2: No encoded ID provided');
      //   this.router.navigate(['/error/guid-missing']);
      //   return;
      // }
      
      // Decode the company GUID from the encoded ID
      this.companyGuid = this.companyIdService.decode(encodedId);
      if(encodedSupplierId != null){
        this.supplierId = environment.MIXPANEL_SUPPLIER_ID_PREFIX + this.companyIdService.decode(encodedSupplierId);
      }

      if(editMode != null){
        this.editMode = 'true';
        await this.previewService.setEditMode(true, this.companyGuid || undefined);
      }

      const decodedGuid = this.companyIdService.decode(encodedGuid);      
      // if (!this.companyGuid) {
      //   console.error('FormBuilderV2: Invalid company GUID');
      //   this.router.navigate(['/error/guid-missing']);
      //   return;
      // }
      
      // Fetch supplier metadata from backend (moved from homepage component)
      try {
        // const supplierMetadata = await this.aiContentService.fetchSupplierMetadata(encodedId);
        const supplierMetadata = await this.aiContentService.fetchSupplierMetadataByGuid(decodedGuid || '');

        console.log('FormBuilderV2: Supplier metadata fetched:', supplierMetadata);
        // CRITICAL: Check if supplierMetadata is undefined/null
        if (!supplierMetadata ) {
          console.error('FormBuilderV2: Supplier metadata is undefined or null');
          this.warningMessage = 'Unable to load company information. Please try again or contact support.';
          this.showWarningPopup = true;
          return; // Stop further execution
        }
        
        // Map and save to IndexedDB
        if (supplierMetadata && this.companyGuid) {
          const metadata = supplierMetadata.metadata;
          if(metadata && !metadata.company_name) {
            console.error('FormBuilderV2: Company name is undefined or null');
            this.warningMessage = 'Unable to load company information. Please try again or contact support.';
            this.showWarningPopup = true;
            return; // Stop further execution
          }
          const contact = Array.isArray(supplierMetadata.contact_metadata) ? supplierMetadata.contact_metadata[0] : null;
          const supplierData: SupplierData = this.mapApiToSupplierData(metadata, contact);
          await this.indexedDBService.setUserSupplierData(this.companyGuid, supplierData);
          console.log('FormBuilderV2: SupplierData saved to IndexedDB:', supplierData);
          
          // Store company name from metadata for direct access
          this.companyNameFromMetadata = metadata?.company_name;
        }
        
        // If company_guid is present in the response, store it in localStorage
        if (supplierMetadata && supplierMetadata.company_guid && isPlatformBrowser(this.platformId)) {
          console.log('FormBuilderV2: company_guid from metadata:', supplierMetadata.company_guid);
          localStorage.setItem('company_guid', supplierMetadata.company_guid);
        }
      } catch (fetchError) {
        console.error('FormBuilderV2: Error fetching supplier metadata:', fetchError);
        this.warningMessage = 'Unable to load company information. Please try again or contact support.';
        this.showWarningPopup = true;
        return; // Stop further execution
      }
      
      // Set company GUID in service
      this.formBuilderV2Service.setCompanyGuid(this.companyGuid || '');
      
      // CRITICAL FIX: Set company GUID in preview service for IndexedDB operations
      this.previewService.setCompanyGuid(this.companyGuid || '');
      
      // Subscribe to form data changes BEFORE loading initial data
      this.formBuilderV2Service.formData$.pipe(
        takeUntil(this.destroy$)
      ).subscribe((data: any) => {
        console.log('FormBuilderV2: Received form data from service:', data);
        // Only update if we have meaningful data
        if (data && Object.keys(data).length > 0) {
          this.formData = { ...this.formData, ...data };
          // Update local isDeploying state from form data
          this.isDeploying = data.isDeploying || false;
          console.log('FormBuilderV2: Updated form data:', this.formData);
          
          // Check if generate buttons should be enabled after loading data
          this.checkGenerateButtonState();
        }
      });
      
      // Load saved form data from IndexedDB
      await this.formBuilderV2Service.loadFromIndexedDB();
      
      // CRITICAL FIX: Ensure form data is properly synchronized after loading
      const currentFormData = this.formBuilderV2Service.getFormData();
      if (currentFormData && Object.keys(currentFormData).length > 0) {
        this.formData = { ...this.formData, ...currentFormData };
        console.log('FormBuilderV2: Synchronized form data after IndexedDB load:', this.formData);
        
        // ✅ CRITICAL FIX: Set the theme in preview service for ALL themes (predefined and custom)
        if (this.formData.theme) {
          console.log('FormBuilderV2: Setting theme in preview service:', this.formData.theme);
          this.previewService.setTheme(this.formData.theme);
          
          // CRITICAL FIX: Set custom theme color in preview service if theme is 'custom'
          if (this.formData.customThemeColor) {
            console.log('FormBuilderV2: Setting custom theme color in preview service:', this.formData.customThemeColor);
            this.previewService.setCustomThemeColor(this.formData.customThemeColor);
          }
        }
        
        // ✅ CRITICAL FIX: Restore completion percentage from form data
        if (currentFormData.completionPercentage !== undefined) {
          this.completionPercentage = currentFormData.completionPercentage;
          console.log('FormBuilderV2: Restored completion percentage from IndexedDB:', this.completionPercentage);
        }
        
        // this.checkGenerateButtonState();
      }
      
      // 🆕 RESTORE SAVED DATA FROM BACKEND API
      if (this.companyGuid) {
        await this.restoreSavedDataFromBackend();
      }
      
      // Initialize with default template
      const templateToUse = this.formData.template > 0 ? this.formData.template : 1;
      
      // Set the template in the service
      await this.formBuilderV2Service.updateTemplate(templateToUse);
      
      // CRITICAL FIX: Initialize preview with restoration (combines initialization + restoration)
      // console.log('FormBuilderV2: Initializing preview with content restoration for template:', templateToUse);
      // await this.previewService.initializePreviewWithRestoration(templateToUse, this.companyGuid || '');
      
      // CRITICAL FIX: Restore edit mode state from IndexedDB
      // console.log('FormBuilderV2: Restoring edit mode from IndexedDB...');
      // await this.previewService.restoreEditModeFromIndexedDB(this.companyGuid || '');

       // 🚫 REMOVED: Initialize preview with restoration - this will be moved to onGenerateCapabilities
    // Instead, just initialize the basic preview without restoration
    console.log('FormBuilderV2: Initializing basic preview for template:', templateToUse);
    await this.previewService.initializePreview(templateToUse, false);
    
    // ✅ CRITICAL FIX: Update preview with form data after initialization to ensure custom theme color is applied
    console.log('FormBuilderV2: Updating preview with form data after initialization');
      this.updatePreviewWithFormData();
      
      // DEBUG: Show all stored tag replacements to help identify missing tags
      // console.log('FormBuilderV2: Debugging stored tag replacements...');
      // await this.formBuilderV2Service.debugTagReplacements();
      
      // VALIDATE: Check if all expected equipment tags are present
      // console.log('FormBuilderV2: Validating equipment tags...');
      // await this.formBuilderV2Service.validateEquipmentTags();
      
      // CRITICAL FIX: Ensure preview is properly updated after initialization
      // This fixes the "Loading preview..." issue on first load
      // await this.ensurePreviewIsLoaded();
      
      // NOTE: Logo will be loaded and saved only during AI content generation, not during initialization
      // This prevents premature saving of COMPANY_LOGO tag to IndexedDB
      
      // console.log('FormBuilderV2: Initialized with company GUID:', this.companyGuid);
      
    } catch (error) {
      console.error('FormBuilderV2: Error during initialization:', error);
      this.router.navigate(['/error/guid-missing']);
    }
  }

  
  // Helper method to convert contact data to tag replacements and save to IndexedDB
  private async saveContactDataToDictContent(): Promise<void> {
    if (!this.companyGuid) return;

    const tagReplacements: any[] = [];
    // Combine address fields
    const addressParts = [];
    if (this.formData.contact.streetAddress) addressParts.push(this.formData.contact.streetAddress);
    if (this.formData.contact.city) addressParts.push(this.formData.contact.city);
    if (this.formData.contact.state) addressParts.push(this.formData.contact.state);
    if (this.formData.contact.zipCode) addressParts.push(this.formData.contact.zipCode);

    const dictContent = await this.indexedDBService.getDictContent(this.companyGuid);

    // Convert contact data to tag replacements
    if (this.formData.contact.email && this.formData.contact.phone && dictContent) {
      dictContent.CompanyInfo.COMPANY_EMAIL = this.formData.contact.email ;
      
      // Format phone number for display in dict.js
      const formattedPhone = PhoneFormatterUtil.formatPhoneNumber(this.formData.contact.phone);
      dictContent.CompanyInfo.COMPANY_PHONE = formattedPhone;
      
      if (addressParts.length > 0) {
        dictContent.CompanyInfo.COMPANY_ADDRESS = addressParts.join(', ');
      }
    }
    if(dictContent) {
      await this.indexedDBService.storeDictContent(this.companyGuid, dictContent);
      await this.previewService.updateDictJsFile(dictContent);
    }

    await this.previewService.applyAIContent(tagReplacements, this.companyGuid);
  }

  /**
   * Synchronize form data with stored tag replacements when opening contact section
   * This ensures the form fields show the correct values from stored tag replacements
   */
  private async synchronizeContactDataFromTagReplacements(): Promise<void> {
    if (!this.companyGuid) return;

    try {
      // Get stored tag replacements from IndexedDB
      const storedTagReplacements = await this.indexedDBService.getTagReplacements(this.companyGuid);
      
      if (storedTagReplacements && storedTagReplacements.length > 0) {
        let hasChanges = false;
        
        // Update form data with stored tag replacement values
        for (const replacement of storedTagReplacements) {
          switch (replacement.tagName) {
            case 'COMPANY_EMAIL':
              if (replacement.replacementHTML && replacement.replacementHTML !== this.formData.contact.email) {
                this.formData.contact.email = replacement.replacementHTML;
                hasChanges = true;
              }
              break;
            case 'COMPANY_PHONE':
              if (replacement.replacementHTML && replacement.replacementHTML !== this.formData.contact.phone) {
                this.formData.contact.phone = replacement.replacementHTML;
                hasChanges = true;
              }
              break;
            case 'COMPANY_ADDRESS':
              if (replacement.replacementHTML && replacement.replacementHTML !== this.formData.contact.streetAddress) {
                // Parse the combined address back into individual fields
                const addressParts = replacement.replacementHTML.split(', ');
                if (addressParts.length >= 1) {
                  this.formData.contact.streetAddress = addressParts[0];
                }
                if (addressParts.length >= 2) {
                  this.formData.contact.city = addressParts[1];
                }
                if (addressParts.length >= 3) {
                  this.formData.contact.state = addressParts[2];
                }
                if (addressParts.length >= 4) {
                  this.formData.contact.zipCode = addressParts[3];
                }
                hasChanges = true;
              }
              break;
          }
        }
        
        if (hasChanges) {
          console.log('FormBuilderV2: Contact form data synchronized with stored tag replacements');
        }
      }
    } catch (error) {
      console.warn('Failed to synchronize contact data from tag replacements:', error);
    }
  }

  // Event handlers for FormBuilderPreviewComponent
  openInlineEditor(event: ContentEditRequest): void {
    // ✅ Use current active section as the mapping (much simpler!)
    const currentSection = this.currentActiveSection;
    
    if (currentSection) {
      // Disable editing when theme section is active
      if (currentSection === 'template') {
        return;
      }
      
      
      // Set section-specific state immediately
      this.isEditingContent[currentSection] = true;
      this.currentContentEditRequest[currentSection] = {
        tagName: event.tagName,
        currentContent: event.currentContent,
        section: currentSection
      };
    }
  }


  // Get current supplier data for the preview component
  getCurrentSupplierData(): any {
    // Get the current semantic content to find contact data
    const semanticContent = this.previewService.getCurrentSemanticContent();
    
    // CRITICAL FIX: Use stored tag replacements for contact data instead of formData.contact
    // This ensures consistency between tag replacements and supplier data placeholders
    const contactEmail = semanticContent['COMPANY_EMAIL'] || this.formData.contact.email;
    const phoneNumber = semanticContent['COMPANY_PHONE'] || this.formData.contact.phone;
    const streetAddress = semanticContent['COMPANY_ADDRESS'] || this.formData.contact.streetAddress;
    
    // Get company name directly from stored metadata
    // This ensures we use the original company name from the backend metadata
    const companyName = this.companyNameFromMetadata;
    
    // Note: This method is synchronous, so we can't use the async helper method here
    // Logo URL is now handled through tag replacements, not SupplierData interface
    return {
      companyName: companyName, // Now properly set directly from backend metadata
      contactEmail: contactEmail,
      phoneNumber: phoneNumber,
      streetAddress: streetAddress,
      city: this.formData.contact.city,
      countryRegion: this.formData.contact.state,
      postalCode: this.formData.contact.zipCode,
      aboutContent: this.formData.aboutUs,
      capabilities: this.formData.capabilities.join(', '),
      equipment: this.formData.equipment,
      websiteURL: '',
      streetAddress2: '',
      logoURL: '' // Logo URL is now handled through tag replacements
    };
  }

  /**
   * Helper method to get the existing company name from IndexedDB
   * This preserves the company name that was fetched from DynamoDB
   */
  private async getExistingCompanyName(): Promise<string> {
    try {
      if (!this.companyGuid) {
        return '';
      }
      
      const existingSupplierData = await this.indexedDBService.getUserSupplierData(this.companyGuid);
      return existingSupplierData.companyName;
    } catch (error) {
      console.warn('Failed to get existing company name from IndexedDB:', error);
      return '';
    }
  }

  /**
   * Helper method to get the current logo URL from IndexedDB or template
   * This ensures the logo URL persists during AI content generation
   */
  private async getCurrentLogoURL(): Promise<string> {
    try {
      // Always get the default template logo as fallback
      const defaultLogoURL = this.websiteFileService.getBlobUrl('images/logo-placeholder.png');
      
      if (!this.companyGuid) {
        // If no company GUID, return default template logo
        return defaultLogoURL || '';
      }
      
      // Get logo from tag replacements instead of supplier data
      const storedTagReplacements = await this.indexedDBService.getTagReplacements(this.companyGuid);
      const logoReplacement = storedTagReplacements?.find(replacement => replacement.tagName === 'IMG_COMPANY_LOGO');
      let logoURL = logoReplacement?.replacementHTML || '';
      
      // If no logo URL in tag replacements, use default template logo
      if (!logoURL) {
        if (defaultLogoURL) {
          logoURL = defaultLogoURL;
          console.log('FormBuilderV2: Using default template logo for AI content generation:', logoURL.substring(0, 50) + '...');
        }
    } else {
        console.log('FormBuilderV2: Using existing logo URL from tag replacements for AI content generation:', logoURL.substring(0, 50) + '...');
      }
      
      return logoURL;
    } catch (error) {
      console.warn('Failed to get current logo URL, using default:', error);
      // Fallback to default template logo
      const defaultLogoURL = this.websiteFileService.getBlobUrl('images/logo-placeholder.png');
      return defaultLogoURL || '';
  }
}


  // V2 Inline Editor Event Handlers
  onV2ContentUpdated(event: { tagName: string; newContent: string }, section: string): void {
    // Apply the content update to the preview
    this.previewService.updateContentInPreview(event.tagName, event.newContent, this.companyGuid || undefined)
      .then(() => {
        // Exit edit mode
        this.closeContentEditor(section);
      })
      .catch((error) => {
        console.error('FormBuilderV2: Error updating content in preview:', error);
      });
  }

  onV2ContentEditCancelled(section: string): void {
    // Exit edit mode without saving
    this.closeContentEditor(section);
  }




  // Helper methods for toggling between editors
  private closeContentEditor(section: string): void {
    if (this.isEditingContent[section]) {
      this.isEditingContent[section] = false;
      this.currentContentEditRequest[section] = null;
    }
  }




  // Clear all edit modes (used when navigating between sections)
  private clearAllEditModes(): void {
    // Clear content edit modes
    Object.keys(this.isEditingContent).forEach(section => {
      this.closeContentEditor(section);
    });

  }

  // Get current semantic content for the preview component
  getCurrentSemanticContent(): { [tagName: string]: string } {
    return this.previewService.getCurrentSemanticContent();
  }

  // Cached properties for each section (performance optimization)
  private imageTagsCache: {
    aboutUs: string[];
    capabilities: string[];
    logo: string[];
  } = { aboutUs: [], capabilities: [], logo: [] };


  // Public getters for template (cached properties)
  get availableAboutUsImageTags(): string[] {
    return this.imageTagsCache.aboutUs;
  }
  
  get availableCapabilitiesImageTags(): string[] {
    return this.imageTagsCache.capabilities;
  }
  
  get availableLogoImageTags(): string[] {
    return this.imageTagsCache.logo;
  }

  // Get only IMG tags from semantic content (more efficient)
  getCurrentImageTags(currentActiveSection?: string): { [tagName: string]: string } {
    // Use the new optimized function from PreviewV2Service with section parameter
    return this.previewService.getCurrentImageSemanticContent(this.currentActiveSection!);
  }

  // Get available image tag names only (legacy method - now uses cache)
  getAvailableImageTagNames(currentActiveSection?: string): string[] {
    // Use the new optimized function from PreviewV2Service with section parameter
    const imageTagNames = this.previewService.getCurrentImageTagNames(this.currentActiveSection!);
    console.log(`🔍 FormBuilderV2: Available image tag names for section ${currentActiveSection || 'all'}:`, imageTagNames);
    
    // Debug: Also log the full image semantic content
    const imageSemanticContent = this.previewService.getCurrentImageSemanticContent(this.currentActiveSection!);
    console.log(`🔍 FormBuilderV2: Full image semantic content for section ${currentActiveSection || 'all'}:`, imageSemanticContent);
    
    return imageTagNames;
  }

  // SIMPLIFIED: Direct file upload without preview component
  triggerImageUploaderForTag(tagName: string): void {
    console.log('🚀 FormBuilderV2: Triggering direct file upload for tag:', tagName);
    
    // Create hidden file input for direct file selection
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.style.display = 'none';
    
    fileInput.onchange = (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (file) {
        this.handleDirectImageUpload(tagName, file);
      }
    };
    
    // Trigger file selection dialog
    document.body.appendChild(fileInput);
    fileInput.click();
    document.body.removeChild(fileInput);
  }

  // Handle direct image upload with cropping and S3 upload
  private async handleDirectImageUpload(tagName: string, file: File): Promise<void> {
    console.log('🚀 FormBuilderV2: Processing direct upload for tag:', tagName, 'file:', file.name);
    
    // Set uploading state
    this.isUploadingImage[tagName] = true;
    this.cdr.detectChanges();
    
    try {
      // 1. Validate file
      if (!this.validateImageFile(file)) {
        this.isUploadingImage[tagName] = false;
        this.cdr.detectChanges();
        return;
      }
      
      // 2. Get placeholder dimensions for cropping
      const placeholderDimensions = await this.getPlaceholderDimensions(tagName);
      console.log('🚀 FormBuilderV2: Placeholder dimensions:', placeholderDimensions);
      
      // 3. Process image (resize if needed)
      let processedFile = file;
      if (placeholderDimensions && tagName !== 'IMG_COMPANY_LOGO') {
        console.log('🚀 FormBuilderV2: Resizing image to fit placeholder dimensions');
        processedFile = await this.resizeImageToFitPlaceholder(file, placeholderDimensions);
      }
      
      // 4. Upload to S3
      console.log('🚀 FormBuilderV2: Uploading to S3...');
      const response = await firstValueFrom(this.imageUploadService.uploadImage(
        this.companyGuid!,
        processedFile,
        tagName
      ));
      
      if (!response) {
        throw new Error('No response received from upload service');
      }
      
      console.log('🚀 FormBuilderV2: S3 upload successful:', response);
      
      // 5. Update preview immediately
      await this.previewService.updateImageInPreview(
        tagName, 
        (response as any).s3_url, 
        this.companyGuid || undefined, 
        file.name
      );
      
      // 6. Update form data and tracking
      this.updateFormDataWithUploadedImage(tagName);

      this.updateSupplierData();
      
      console.log('🚀 FormBuilderV2: Direct upload completed successfully');
      
    } catch (error) {
      console.error('🚀 FormBuilderV2: Direct upload failed:', error);
      this.toastr.error(`Image upload failed`, 'Upload Error');
    } finally {
      // Clear uploading state
      this.isUploadingImage[tagName] = false;
      this.cdr.detectChanges();
    }
  }

  // Validate image file
  private validateImageFile(file: File): boolean {
    // Check file type
    if (!file.type.startsWith('image/')) {
      this.toastr.error('Please select a valid image file', 'Invalid File Type');
      return false;
    }

    return true;
  }

  // Get placeholder dimensions for cropping
  private async getPlaceholderDimensions(tagName: string): Promise<{width: number, height: number} | null> {
    try {
      const currentImageSrc = this.previewService.getCurrentImageSource(tagName, this.currentActiveSection || undefined);
      
      if (!currentImageSrc) return null;
      
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          resolve({
            width: img.width,
            height: img.height
          });
        };
        img.onerror = () => {
          resolve(null);
        };
        img.src = currentImageSrc;
      });
    } catch (error) {
      console.error('FormBuilderV2: Error getting placeholder dimensions:', error);
      return null;
    }
  }

  // Resize image to fit placeholder dimensions (preserves cropping logic)
  private async resizeImageToFitPlaceholder(file: File, dimensions: {width: number, height: number}): Promise<File> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }
      
      const img = new Image();
      img.onload = () => {
        try {
          // Smart fill approach: Always use exact placeholder dimensions
          const scaleX = dimensions.width / img.width;
          const scaleY = dimensions.height / img.height;
          const scale = Math.max(scaleX, scaleY); // Use larger scale to cover entire area
          
          // Calculate scaled dimensions
          const scaledWidth = img.width * scale;
          const scaledHeight = img.height * scale;
          
          // Calculate offset to center the scaled image
          const offsetX = (dimensions.width - scaledWidth) / 2;
          const offsetY = (dimensions.height - scaledHeight) / 2;
          
          // Set canvas size to exact placeholder dimensions
          canvas.width = dimensions.width;
          canvas.height = dimensions.height;
          
          // Configure canvas for high quality
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          
          // Draw image scaled and centered to fill entire canvas
          ctx.drawImage(img, offsetX, offsetY, scaledWidth, scaledHeight);
          
          // Convert to blob
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const resizedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now()
                });
                resolve(resizedFile);
              } else {
                reject(new Error('Canvas to blob conversion failed'));
              }
            },
            'image/jpeg',
            0.9 // 90% quality
          );
          
        } catch (error) {
          console.error('FormBuilderV2: Error during image resize:', error);
          reject(error);
        }
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image for resizing'));
      };
      
      img.src = URL.createObjectURL(file);
    });
  }

  // Update form data with uploaded image
  private updateFormDataWithUploadedImage(tagName: string): void {
    const section = this.currentActiveSection;
    if (section && !(this.formData.uploadedImagesBySection as any)[section]?.includes(tagName)) {
      if (!(this.formData.uploadedImagesBySection as any)[section]) {
        (this.formData.uploadedImagesBySection as any)[section] = [];
      }
      (this.formData.uploadedImagesBySection as any)[section].push(tagName);
      console.log(`📸 FormBuilderV2: Image ${tagName} added to uploaded list for section ${section}`);
      
      // Update service to persist formData changes automatically
      this.formBuilderV2Service.updateFormData(this.formData);
    }
  }

  // Check if an image is currently uploading
  isImageUploading(tagName: string): boolean {
    return this.isUploadingImage[tagName] || false;
  }

  // Get current image source for a specific tag (optimized for IMG tags only)
  private getCurrentImageSource(tagName: string, currentActiveSection?: string): string {
    // Use the new optimized function from PreviewV2Service with section parameter
    return this.previewService.getCurrentImageSource(tagName, currentActiveSection);
  }

  // Cache refresh method - updates image tags cache for all sections
  private refreshImageTagsCache(): void {
    this.imageTagsCache = {
      aboutUs: this.previewService.getCurrentImageTagNames('about-us'),
      capabilities: this.previewService.getCurrentImageTagNames('capabilities'),
      logo: this.previewService.getCurrentImageTagNames('logo')
    };
    console.log('🔄 FormBuilderV2: Image tags cache refreshed:', this.imageTagsCache);
  }

  // Validation methods for image upload completion
  getAvailableImagesForSection(section: string): string[] {
    switch(section) {
      case 'about-us': return this.availableAboutUsImageTags;
      case 'capabilities': return this.availableCapabilitiesImageTags;
      case 'logo': return this.availableLogoImageTags;
      default: return [];
    }
  }

  isSectionImagesComplete(section: string): boolean {
    const availableImages = this.getAvailableImagesForSection(section);
    const uploadedImages = (this.formData.uploadedImagesBySection as any)[section] || [];
    
    // If no images are available for this section, consider it complete
    if (availableImages.length === 0) {
      return true;
    }
    
    return availableImages.every(imageTag => 
      uploadedImages.includes(imageTag)
    );
  }

  isImageUploaded(tagName: string): boolean {
    const currentSection = this.currentActiveSection;
    if (!currentSection || !(this.formData.uploadedImagesBySection as any)[currentSection]) {
      return false;
    }
    return (this.formData.uploadedImagesBySection as any)[currentSection].includes(tagName);
  }

  /**
   * Get the filename for a specific image tag from stored tag replacements
   * @param tagName - The image tag name (e.g., 'IMG_ABOUT_1')
   * @returns The filename if available, null otherwise
   */
  getImageFileName(tagName: string): string | null {
    // Get the stored tag replacements from the preview service
    const tagReplacements = this.previewService.getStoredTagReplacements();
    
    // Find the specific tag replacement for this image
    const imageReplacement = tagReplacements.find(replacement => 
      replacement.tagName === tagName && replacement.fileName
    );
    
    return imageReplacement?.fileName || null;
  }

  /**
   * Get the display text for image upload status with filename truncation
   * @param tagName - The image tag name (e.g., 'IMG_ABOUT_1')
   * @returns Display text: filename (truncated if needed), 'Uploaded', or 'No File Chosen'
   */
  getImageDisplayText(tagName: string): string {
    if (!this.isImageUploaded(tagName)) {
      return 'No File Chosen';
    }
    
    const fileName = this.getImageFileName(tagName);
    if (fileName) {
      // Truncate long filenames to keep UI clean
      const maxLength = 25;
      if (fileName.length > maxLength) {
        return fileName.substring(0, 17) + '...';
      }
      return fileName;
    }
    
    return 'Uploaded';
  }

  getSectionValidationStatus(section: string): {
    isComplete: boolean;
    uploadedCount: number;
    totalCount: number;
    missingImages: string[];
  } {
    const availableImages = this.getAvailableImagesForSection(section);
    const uploadedImages = (this.formData.uploadedImagesBySection as any)[section] || [];
    
    const missingImages = availableImages.filter(img => 
      !uploadedImages.includes(img)
    );
    
    return {
      isComplete: missingImages.length === 0,
      uploadedCount: uploadedImages.length,
      totalCount: availableImages.length,
      missingImages: missingImages
    };
  }

  getSectionValidationTooltip(section: string): string {
    const status = this.getSectionValidationStatus(section);
    
    if (status.isComplete) {
      return 'All images uploaded. Ready to complete section.';
    }
    
    if (status.totalCount === 0) {
      return 'No images required for this section.';
    }
    
    const missingNames = status.missingImages
      .map(img => img.replace('IMG_', '').replace('_', ' '))
      .join(', ');
      
    return `Please upload all images before completing: ${missingNames}`;
  }

  // APPROACH 4: Direct HTML parsing (alternative method)
  triggerImageUploaderForTagFromHTML(tagName: string): void {
    console.log('FormBuilderV2: Triggering image uploader for tag from HTML:', tagName);
    
    // Get current image source by parsing HTML directly
    const currentImageSrc = this.getCurrentImageSourceFromHTML(tagName);
    
    const uploadRequest: ImageUploadRequest = {
      tagName: tagName,
      currentImageSrc: currentImageSrc,
      elementPosition: {
        x: 0,
        y: 0,
        width: 300,
        height: 200
      }
    };
    
    // Direct upload - no V2 component needed
    this.triggerImageUploaderForTag(uploadRequest.tagName);
  }

  // Get current image source by parsing HTML directly
  private getCurrentImageSourceFromHTML(tagName: string): string {
    // Get the current template HTML from preview service
    // Note: This is a simplified approach - in production you might want to subscribe to the observable
    const currentPreview = (this.previewService as any).previewUpdateSubject?.value;
    if (currentPreview?.templateHtml) {
      try {
        // Parse HTML to find the image tag
        const parser = new DOMParser();
        const doc = parser.parseFromString(currentPreview.templateHtml, 'text/html');
        const imgElement = doc.querySelector(`img[tag="${tagName}"]`);
        const src = imgElement?.getAttribute('src') || '';
        
        console.log(`Found image source for ${tagName} in HTML:`, src);
        return src;
      } catch (error) {
        console.error(`Error parsing HTML for tag ${tagName}:`, error);
      }
    }
    
    console.log(`No HTML found for tag ${tagName}, returning empty string`);
    return '';
  }

  // Get all IMG tags from HTML parsing (Approach 4 alternative)
  getImageTagsFromHTML(): { [tagName: string]: string } {
    const imageTags: { [tagName: string]: string } = {};
    const currentPreview = (this.previewService as any).previewUpdateSubject?.value;
    
    if (currentPreview?.templateHtml) {
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(currentPreview.templateHtml, 'text/html');
        const imgElements = doc.querySelectorAll('img[tag^="IMG_"]');
        
        imgElements.forEach(img => {
          const tagName = img.getAttribute('tag');
          const src = img.getAttribute('src');
          if (tagName && src) {
            imageTags[tagName] = src;
          }
        });
        
        console.log('Found IMG tags from HTML:', Object.keys(imageTags));
      } catch (error) {
        console.error('Error parsing HTML for IMG tags:', error);
      }
    }
    
    return imageTags;
  }

  // Click outside handler for dropdown
  private addClickOutsideHandler(): void {
    if (isPlatformBrowser(this.platformId)) {
      document.addEventListener('click', this.handleClickOutside.bind(this));
    }
  }

  private removeClickOutsideHandler(): void {
    if (isPlatformBrowser(this.platformId)) {
      document.removeEventListener('click', this.handleClickOutside.bind(this));
    }
  }

  private handleClickOutside(event: Event): void {
    const target = event.target as HTMLElement;
    const dropdownContainer = target.closest('.dropdown-container');
    
    if (!dropdownContainer && this.isCapabilitiesDropdownOpen) {
      this.isCapabilitiesDropdownOpen = false;
    }
  }

  // Home methods
  onHeroTitleChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.formData.home.heroTitle = target.value;
    console.log('Hero title updated:', this.formData.home.heroTitle);
    
    // Update service
    this.formBuilderV2Service.updateHeroTitle(target.value);
  }

  onHeroSubtitleChange(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.formData.home.heroSubtitle = target.value;
    console.log('Hero subtitle updated:', this.formData.home.heroSubtitle);
    
    // Update service
    this.formBuilderV2Service.updateHeroSubtitle(target.value);
  }

  onHeroDescriptionChange(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.formData.home.heroDescription = target.value;
    console.log('Hero description updated:', this.formData.home.heroDescription);
    
    // Update service
    this.formBuilderV2Service.updateHeroDescription(target.value);
  }

  completeHome(): void {
    console.log('Home completed:', this.formData.home);
    this.isHomeCompleted = true;
    
    // Save completion state to IndexedDB
    this.saveSectionCompletion('isHomeCompleted', true);
    
    // Close all sections after completion - let user decide what to edit next
    this.currentActiveSection = null;
    this.saveCurrentSectionState();
    
    // Keep preview on home page to show completed content
    this.updatePreviewForSection('home');
    console.log('Home section completed - all sections now available for editing');
  }

  // Template/Theme methods
  async onCompleteTheme(): Promise<void> {
    this.isThemeCompleting = true;
    this.isThemeCompleted = true;
    
    // Save completion state to IndexedDB
    this.saveSectionCompletion('isThemeCompleted', true);
    
    // Update website progress
    this.updateWebsiteProgress('about-us');
    
    await this.updateSupplierData();  
    // Close theme section and open capabilities section
    this.currentActiveSection = 'about-us';
    this.saveCurrentSectionState();
    this.updatePreviewForSection('about-us');

    this.isThemeCompleting = false;
  }


  async onCompleteLogo(): Promise<void> {
    this.isLogoCompleting = true;
    this.isLogoCompleted = true;
    this.isLogoCompleted = true;
    await this.updateSupplierData();
    // Save completion state to IndexedDB
    this.saveSectionCompletion('isLogoCompleted', true);
    
    // Update website progress
    this.updateWebsiteProgress('publish');
    
    // Get the next section to open automatically
    const nextSection = this.getNextSection('logo');
    
    if (nextSection) {
      // Open the next section automatically
      this.currentActiveSection = nextSection;
      this.updatePreviewForSection(nextSection);
    } else {
      // All sections completed
      this.currentActiveSection = nextSection;
      this.saveCurrentSectionState();
      this.updatePreviewForSection('logo');
    }
    this.isLogoCompleting = false;
  }

  // Manual complete logo section (from complete button)
  async onCompleteLogoSection(): Promise<void> {
    console.log('FormBuilderV2: Manual logo section completion triggered');
    
    // Check if all required images are uploaded
    if (!this.isSectionImagesComplete('logo')) {
      console.warn('FormBuilderV2: Cannot complete logo section - not all images uploaded');
      return;
    }

    this.isLogoCompleting = true;
    this.isLogoCompleted = true;
    
    try {
      // Save form data and update supplier data
      
      // Save completion state to IndexedDB
      this.saveSectionCompletion('isLogoCompleted', true);
      
      // Update website progress
      this.updateWebsiteProgress('publish');
      
      console.log('FormBuilderV2: Logo section completed successfully');
      
      this.updateSupplierData();
      // Get the next section to open automatically
      const nextSection = this.getNextSection('logo');
      
      if (nextSection) {
        // Open the next section automatically
        this.currentActiveSection = nextSection;
        this.updatePreviewForSection(nextSection);
      } else {
        // All sections completed - close current section
        this.currentActiveSection = null;
        this.saveCurrentSectionState();
        this.updatePreviewForSection('logo');
      }
      
    } catch (error) {
      console.error('FormBuilderV2: Error completing logo section:', error);
      this.isLogoCompleted = false; // Revert on error
    } finally {
      this.isLogoCompleting = false;
    }
  }



  /**
   * Control edit mode based on current section and AI content generation status
   */
  private updateEditModeForCurrentSection(): void {
    // Only control edit mode if AI content has been generated
    const currentSection = this.currentActiveSection;

    if (!this.formBuilderV2Service.hasAIContentBeenGenerated() && currentSection !== 'about-us') {
      this.previewService.setEditMode(false, this.companyGuid || undefined);
      return;
    }
    
    if (currentSection === 'template' || (currentSection === 'about-us' && !this.formBuilderV2Service.hasAboutContentBeenGenerated()) || (currentSection === 'capabilities' && !this.formBuilderV2Service.hasCapabilitiesContentBeenGenerated())) {
      // Disable edit mode when on theme section
      this.previewService.setEditMode(false, this.companyGuid || undefined);
    } else if (currentSection && ['capabilities', 'about-us', 'contact', 'logo'].includes(currentSection)) {
      // Enable edit mode when on other sections
      this.previewService.setEditMode(true, this.companyGuid || undefined);
    } 
  }

  private initializeAccordion(): void {
    // Initialize accordion functionality
    
    // Only run in browser environment
    if (isPlatformBrowser(this.platformId)) {
      // Expand the template section by default
      setTimeout(() => {
        const templateSection = document.querySelector('.accordion-section:first-child .accordion-content');
        if (templateSection) {
          templateSection.classList.add('active');
        }
      }, 100);
    }
  }

  // Template selection methods
  async onTemplateClick(templateId: number): Promise<void> {
    console.log('FormBuilderV2: Template clicked:', templateId);
    console.log('FormBuilderV2: Previous template:', this.formData.template);
    console.log('FormBuilderV2: Current form data:', this.formData);
    
    // Don't do anything if same template is clicked
    if (templateId === this.formData.template) {
      console.log('FormBuilderV2: Same template clicked, ignoring');
      return;
    }
    
    // Track template selection
    this.mixPanelService.trackTemplateSelection(templateId);
    
    // ADD: Loading timeout fallback (10 seconds) to prevent stuck loading states
    const loadingTimeout = setTimeout(() => {
      if (this.isLoading) {
        console.warn('FormBuilderV2: Template loading timeout, clearing loading state');
        this.isLoading = false;
      }
    }, 20000);
    
    // Show loading state
    this.isLoading = true;
    
    // Update local form data immediately
    this.formData.template = templateId;
    console.log('FormBuilderV2: Updated local form data template to:', this.formData.template);
    
    try {
      // 🆕 RESET SECTION COMPLETIONS ON TEMPLATE CHANGE
      await this.formBuilderV2Service.resetSectionCompletionsOnTemplateChange();
      
      // Reset local completion states
      this.completionPercentage = 0;
      this.isThemeCompleted = false;
      this.isAboutCompleted = false;
      this.isCapabilitiesCompleted = false;
      this.isContactCompleted = false;
      this.isLogoCompleted = false;
      
      // Update service and IndexedDB
      console.log('FormBuilderV2: Updating service with template:', templateId);
      await this.formBuilderV2Service.updateTemplate(templateId);
      
      // Initialize preview with selected template
      console.log('FormBuilderV2: Initializing preview for template:', templateId);
      await this.previewService.initializePreviewWithRestoration(templateId, this.companyGuid || '',this.editMode);
      
      // Apply default logo to preview (without saving to IndexedDB)
      
      // Update form data in service
      this.formBuilderV2Service.updateFormData({ template: templateId });
      
      // Force change detection to update UI
      this.cdr.detectChanges();
      
      console.log('✅ FormBuilderV2: Template change completed successfully');
    } catch (error) {
      console.error('FormBuilderV2: Error during template selection:', error);
      // ENHANCE: Ensure loading state is always cleared on error
      this.isLoading = false;
    } finally {
      // ADD: Clear timeout and ensure loading state is cleared
      clearTimeout(loadingTimeout);
      this.isLoading = false;
    }
  }

  async onColorClick(themeKey: string): Promise<void> {
    if (themeKey === this.formData.theme) {
      return;
    }
    
    // Track theme selection
    this.mixPanelService.trackThemeSelection(themeKey);
    
    this.formData.theme = themeKey;
    
    // Handle default color selection
    if (themeKey === 'default' && this.formData.template) {
      this.customThemeColor = this.templateDefaultColors[this.formData.template];
    } else if (COLOR_THEMES[themeKey]) {
      this.customThemeColor = COLOR_THEMES[themeKey].primary;
    } else {
      // Fallback to a default color
      this.customThemeColor = '#2e58d6';
    }
    
    try {
      await this.formBuilderV2Service.updateTheme(themeKey);
      await this.formBuilderV2Service.updateCustomThemeColor(this.customThemeColor);
      this.previewService.setTheme(themeKey);
      this.previewService.setCustomThemeColor(this.customThemeColor);
      this.updatePreviewWithFormData();
    } catch (error) {
      console.error('FormBuilderV2: Error during color selection:', error);
    }
  }

  // Custom Color Picker Methods
  openCustomColorPicker(): void {
    console.log('FormBuilderV2: Opening custom color picker');
    this.showCustomColorPicker = true;
    
    // Load the current custom color from form data
    if (this.formData.theme === 'custom') {
      this.customThemeColor = this.formData.customThemeColor || '#2e58d6';
      console.log('FormBuilderV2: Loaded existing custom color:', this.customThemeColor);
    } else {
      // Set default color if no custom theme is currently selected
      this.customThemeColor = '#2e58d6';
      console.log('FormBuilderV2: Using default custom color:', this.customThemeColor);
    }

    // Automatically open the native color picker after the modal is rendered
    setTimeout(() => {
      if (this.colorInput && this.colorInput.nativeElement) {
        this.colorInput.nativeElement.click();
      }
    }, 100);
  }

  closeCustomColorPicker(): void {
    this.showCustomColorPicker = false;
  }

  // Method to manually open the color picker (for user convenience)
  openColorPicker(): void {
    if (this.colorInput && this.colorInput.nativeElement) {
      this.colorInput.nativeElement.click();
    }
  }

  onCustomColorChange(): void {
    // Real-time preview updates for custom color picker
    // Update the preview immediately with the new color
    if (this.formData.theme === 'custom') {
      this.previewService.setCustomThemeColor(this.customThemeColor);
      // OPTIMIZATION: Removed duplicate updatePreviewWithFormData() call
      // The preview will be updated when user clicks "Apply Color" in applyCustomColor()
    }
  }

  onCustomColorTextChange(): void {
    // Validate hex color format
    if (this.customThemeColor.match(/^#[0-9A-F]{6}$/i)) {
      // Valid hex color
    } else {
      // Invalid hex color format
    }
  }

  async applyCustomColor(): Promise<void> {
    if (!this.customThemeColor.match(/^#[0-9A-F]{6}$/i)) {
      return;
    }
    
    this.formData.theme = 'custom';
    this.formData.customThemeColor = this.customThemeColor;
    
    try {
      await this.formBuilderV2Service.updateTheme('custom');
      await this.formBuilderV2Service.updateCustomThemeColor(this.customThemeColor);
      
      this.previewService.setTheme('custom');
      this.previewService.setCustomThemeColor(this.customThemeColor);
      
      // FIX: Use updatePreviewWithFormData() instead of direct updatePreview() call
      // This ensures proper supplier data conversion and consistency with individual theme selection
      this.updatePreviewWithFormData();
      
      this.closeCustomColorPicker();
    } catch (error) {
      console.error('FormBuilderV2: Error applying custom color:', error);
    }
  }

  // Section navigation methods
  openSection(section: 'template' | 'capabilities' | 'about-us' | 'contact' | 'home' | 'logo'): void {
    // Clear edit modes when navigating away from sections
    this.clearAllEditModes();
    
    // Accordion behavior: if clicking on the currently active section, close it
    if (this.currentActiveSection === section) {
      // this.currentActiveSection = section;
      // Save the closed state
      // this.saveCurrentSectionState();
      return;
    }
    
    // Otherwise, open the new section (automatically closes others)
    this.currentActiveSection = section;
    
    // Refresh image tags cache when opening a section
    this.refreshImageTagsCache();
    
    // Auto-save current section state
    this.saveCurrentSectionState();
    
    // Update website progress when manually navigating to a section
    // this.updateWebsiteProgress(section);
    
    // ✅ CRITICAL FIX: Update edit mode based on the new section
    this.updateEditModeForCurrentSection();
    
    // Note: Logo section now behaves like other sections - no special handling needed
    
    // Special handling for contact section - ensure contact data is synchronized
    // if (section === 'contact') {
    //   this.synchronizeContactDataFromTagReplacements();
    // }
    
    // Update preview to show the corresponding page
    this.updatePreviewForSection(section);
  }

  /**
   * Get allowed pages for navigation based on current active section
   */
  private getAllowedPagesForSection(section: string): string[] {
    switch (section) {
      case 'template':
        // Theme section: Allow all pages for browsing
        return ['index', 'about', 'capabilities', 'contact', 'quote', '404'];
      
      case 'capabilities':{
        // Capabilities section: Only allow capabilities page if AI content has not been generated
        if(!this.formBuilderV2Service.hasAIContentBeenGenerated()) {
          return ['capabilities'];
        }
        else{
          return ['index', 'about', 'capabilities', 'contact', 'quote', '404'];
        }
      }
      case 'about-us':
        // About Us section: Allow capabilities and about pages
        if(!this.formBuilderV2Service.hasAIContentBeenGenerated()) {
          return ['about'];
        }
        else{
          return ['index', 'about', 'capabilities', 'contact', 'quote', '404'];
        }
      
      case 'contact':
        // Contact section: Allow capabilities and contact pages
        return ['index', 'about', 'capabilities', 'contact', 'quote', '404'];
      
      case 'logo':
        // Logo section: Allow all pages again
        return ['index', 'about', 'capabilities', 'contact', 'quote', '404'];
      
      default:
        // Default: Allow all pages
        return ['index', 'about', 'capabilities', 'contact', 'quote', '404'];
    }
  }

  /**
   * Update preview with contextual navigation restrictions
   */
  updatePreviewForSection(section: string): void {
    // Map form sections to preview pages
    const sectionToPageMap: { [key: string]: string } = {
      'home': 'index',
      'template': 'index',
      'capabilities': 'capabilities',
      'about-us': 'about',
      'contact': 'contact',
      'logo': 'index'
    };
    
    const pageName = sectionToPageMap[section];
    if (pageName) {
      // Get allowed pages for the current section
      const allowedPages = this.getAllowedPagesForSection(section);
      
      // CRITICAL FIX: Set allowed pages BEFORE loading the page
      // This ensures navigation restrictions are applied when the page is loaded
      this.previewService.setAllowedPages(allowedPages);
      
      // ✅ CRITICAL FIX: Update edit mode based on current section
      this.updateEditModeForCurrentSection();
      
      // Load the corresponding page in the preview
      const supplierData = this.getCurrentSupplierData();
      this.previewService.loadTemplatePage(pageName, supplierData, this.companyGuid || undefined);
    }
  }

  private getSectionIndex(section: string): number {
    const sectionOrder = ['template', 'capabilities', 'about-us', 'contact', 'logo'];
    return sectionOrder.indexOf(section);
  }

  /**
   * Get the next section in the progression flow
   * Default progression: template -> capabilities -> about-us -> contact -> logo
   * After capabilities, user can choose between about-us, contact, or logo
   */
  private getNextSection(currentSection: string): string | null {
    const sectionOrder = ['template','about-us', 'capabilities', 'contact', 'logo'];
    const currentIndex = sectionOrder.indexOf(currentSection);
    
    if (currentIndex === -1) {
      return null;
    }
    
    // Special case: After capabilities, default to about-us but allow flexibility
    if ( currentSection === 'logo') {
      // Check if about-us is already completed, then try contact, then logo
      if (!this.isAboutCompleted ) {
        return 'about-us';
      } else if (!this.isContactCompleted) {
        return 'contact';
      } else if (!this.isLogoCompleted) {
        return 'logo';
      }
      return currentSection; // All sections completed
    }
    
    // Normal progression for other sections
    const nextIndex = currentIndex + 1;
    if (nextIndex < sectionOrder.length) {
      const nextSection = sectionOrder[nextIndex];
      
      // Check if the next section is already completed
      switch (nextSection) {
        case 'capabilities':
          return this.isCapabilitiesCompleted ? this.getNextSection('capabilities') : 'capabilities';
        case 'about-us':
          return this.isAboutCompleted ? this.getNextSection('about-us') : 'about-us';
        case 'contact':
          return this.isContactCompleted ? this.getNextSection('contact') : 'contact';
        case 'logo':
          return this.isLogoCompleted ? this.getNextSection('logo') : 'logo';
        default:
          return nextSection;
      }
    }
    
    return currentSection; // No more sections
  }

  // Capabilities methods
  onCapabilitiesChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const selectedOptions = Array.from(target.selectedOptions, (option) => option.value);
    this.formData.capabilities = selectedOptions;
    console.log('Capabilities updated:', this.formData.capabilities);
    
    // Update service
    this.formBuilderV2Service.updateCapabilities(selectedOptions);
    
    // Check if generate buttons should be enabled
    this.checkGenerateButtonState();
    this.markCapabilitiesSelectionChangedIfNeeded();
  }

  // Check if generate content button should be enabled
  checkGenerateButtonState(): void {
    const hasCapabilities = this.formData.capabilities && this.formData.capabilities.length > 0;
    const hasEquipment = this.formData.equipment && this.formData.equipment.trim().length > 0;
    
    // Enable button if both capabilities and equipment are provided
    this.canGenerateContent = Boolean(hasCapabilities && hasEquipment);
    
    // Check About Us generation state
    this.checkGenerateAboutUsButtonState();
  }
  
  // Check if generate About Us content button should be enabled
  checkGenerateAboutUsButtonState(): void {
    // Check if aboutUs field has content
    const hasAboutUsContent = Boolean(this.formData.aboutUs && this.formData.aboutUs.trim().length > 0);
    
    // Enable About Us generation only if aboutUs content is provided
    this.canGenerateAboutUsContent = hasAboutUsContent;
  }

  onEquipmentChange(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.formData.equipment = target.value;
    console.log('Equipment updated:', this.formData.equipment);
    
    // Update service
    this.formBuilderV2Service.updateEquipment(target.value);
    
    // Check if generate buttons should be enabled
    this.checkGenerateButtonState();
  }

  onAdditionalCapabilitiesChange(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.formData.additionalCapabilities = target.value;
    console.log('Additional capabilities updated:', this.formData.additionalCapabilities);
    
    // Update service
    this.formBuilderV2Service.updateAdditionalCapabilities(target.value);
    
    // Check if generate buttons should be enabled
    this.checkGenerateButtonState();
  }

  // Helper method to get complete website template HTML for AI content generation
  private getCompleteTemplateHtml(): string {
    const htmlFiles = ['index.html', 'about.html', 'capabilities.html', 'contact.html', 'quote.html'];
    let completeTemplateHtml = '';
    
    for (const fileName of htmlFiles) {
      const fileContent = this.websiteFileService.getFileContent(fileName);
      if (fileContent && typeof fileContent.content === 'string') {
        completeTemplateHtml += fileContent.content + '\n';
      }
    }
    
    return completeTemplateHtml;
  }

  /**
 * Restore saved data from IndexedDB and apply to preview
 * This method handles the restoration of form data, edit mode state, and tag replacements
 */
  private async restoreSavedDataFromIndexedDB(): Promise<void> {
    try {
      console.log('FormBuilderV2: Starting restoration of saved data from IndexedDB...');
      
      // CRITICAL FIX: Restore edit mode state from IndexedDB
      console.log('FormBuilderV2: Restoring edit mode from IndexedDB...');
      await this.previewService.restoreEditModeFromIndexedDB(this.companyGuid || '');
      
      // Load saved form data from IndexedDB
      await this.formBuilderV2Service.loadFromIndexedDB();
      
      // CRITICAL FIX: Ensure form data is properly synchronized after loading
      const currentFormData = this.formBuilderV2Service.getFormData();
      if (currentFormData && Object.keys(currentFormData).length > 0) {
        this.formData = { ...this.formData, ...currentFormData };
        console.log('FormBuilderV2: Synchronized form data after IndexedDB load:', this.formData);
        
        // CRITICAL FIX: Set custom theme color in preview service if theme is 'custom'
        if (this.formData.theme === 'custom' && this.formData.customThemeColor) {
          console.log('FormBuilderV2: Setting custom theme color in preview service:', this.formData.customThemeColor);
          this.previewService.setCustomThemeColor(this.formData.customThemeColor);
        }
        
        this.checkGenerateButtonState();
        this.checkGenerateAboutUsButtonState();
      }
      
      // �� ADDED: Apply saved tag replacements to preview (this was likely in initializePreviewWithRestoration)
      console.log('FormBuilderV2: Applying saved tag replacements to preview...');
      const savedTagReplacements = await this.indexedDBService.getTagReplacements(this.companyGuid || '');
      if (savedTagReplacements && savedTagReplacements.length > 0) {
        await this.previewService.applyAIContent(savedTagReplacements, this.companyGuid || undefined);
        console.log('FormBuilderV2: Applied', savedTagReplacements.length, 'saved tag replacements');
      }
      
      // DEBUG: Show all stored tag replacements to help identify missing tags
      console.log('FormBuilderV2: Debugging stored tag replacements...');
      await this.formBuilderV2Service.debugTagReplacements();
      
      // VALIDATE: Check if all expected equipment tags are present
      console.log('FormBuilderV2: Validating equipment tags...');
      await this.formBuilderV2Service.validateEquipmentTags();
      
      // CRITICAL FIX: Ensure preview is properly updated after restoration
      await this.ensurePreviewIsLoaded();
      
      console.log('FormBuilderV2: Restoration of saved data completed successfully');
      
    } catch (error) {
      console.error('FormBuilderV2: Error during restoration of saved data:', error);
      // Continue execution even if restoration fails
    }
  }

  onGenerateCapabilitiesContent(): void {
    
    if (this.isGeneratingContent) {
      console.log('FormBuilderV2: Already generating content, ignoring request');
      return;
    }

    this.hasGenerateCapabilitiesBeenClickedSinceSelectionChange = true;
    this.isGeneratingContent = true;
    console.log('FormBuilderV2: Set generating content flag to true');

    // Get complete website template HTML (all pages combined) instead of just capabilities.html
    // This ensures all AI-generated tags can be processed and saved to IndexedDB
    const completeTemplateHtml = this.getCompleteTemplateHtml();
    console.log('FormBuilderV2: Complete template HTML length:', completeTemplateHtml?.length || 0);
    
    if (!completeTemplateHtml || completeTemplateHtml.trim().length === 0) {
      console.error('FormBuilderV2: No template HTML available for content generation');
      this.isGeneratingContent = false;
      return;
    }

    // Prepare supplier data for AI content generation (matching V1 format)
    Promise.all([
      this.getExistingCompanyName(),
      this.getCurrentLogoURL()
    ]).then(async ([existingCompanyName, currentLogoURL]) => {
      const supplierData = {
        companyName: existingCompanyName,
        contactEmail: this.formData.contact.email,
        phoneNumber: this.formData.contact.phone,
        streetAddress: this.formData.contact.streetAddress,
        streetAddress2: '',
        city: this.formData.contact.city,
        countryRegion: this.formData.contact.state,
        postalCode: this.formData.contact.zipCode,
        aboutContent: this.formData.aboutUs,
        capabilities: this.formData.capabilities.join(', '),
        equipment: this.formData.equipment,
        other_capabilities: this.formData.additionalCapabilities,
        websiteURL: '',
        logoURL: currentLogoURL
      };

      this.aiContentService.generateAndApplyDictContent(supplierData, completeTemplateHtml)
        .then(async (result: any) => {
          console.log('FormBuilderV2: AI content generation result:', result);
          if (result && result.hasChanges && result.contentUpdates) {
            console.log('FormBuilderV2: Content updates received:', result.contentUpdates.length);
            
            // Convert content updates to tag replacements format
            const tagReplacements = result.contentUpdates
              .filter((update: any) => update.isChanged)
              .map((update: any) => ({
                tagName: update.tagName,
                replacementHTML: update.newContent
              }));

            console.log('FormBuilderV2: Converted to tag replacements:', tagReplacements.length);
           
            const logoURLToUse = currentLogoURL || this.websiteFileService.getBlobUrl('images/logo-placeholder.png');
            if (logoURLToUse) {
              tagReplacements.push({
                tagName: 'COMPANY_LOGO',
                replacementHTML: logoURLToUse
              });
              console.log('FormBuilderV2: Added COMPANY_LOGO tag to replacements:', logoURLToUse.substring(0, 50) + '...');
            }
            if(existingCompanyName){
              tagReplacements.push({
                tagName: 'COMPANY_NAME',
                replacementHTML: existingCompanyName
              });
            }

            // Add contact information tags
            if (this.formData.contact.email) {
              tagReplacements.push({
                tagName: 'COMPANY_EMAIL',
                replacementHTML: this.formData.contact.email
              });
              console.log('FormBuilderV2: Added COMPANY_EMAIL tag to replacements:', this.formData.contact.email);
            }

            if (this.formData.contact.phone) {
              // Format phone number for tag replacement
              const formattedPhone = PhoneFormatterUtil.formatPhoneNumber(this.formData.contact.phone);
              tagReplacements.push({
                tagName: 'COMPANY_PHONE',
                replacementHTML: formattedPhone
              });
              console.log('FormBuilderV2: Added COMPANY_PHONE tag to replacements:', formattedPhone);
            }

            // Add address tag (combine all address fields)
            const addressParts = [];
            if (this.formData.contact.streetAddress) addressParts.push(this.formData.contact.streetAddress);
            if (this.formData.contact.city) addressParts.push(this.formData.contact.city);
            if (this.formData.contact.state) addressParts.push(this.formData.contact.state);
            if (this.formData.contact.zipCode) addressParts.push(this.formData.contact.zipCode);
            
            if (addressParts.length > 0) {
              tagReplacements.push({
                tagName: 'COMPANY_ADDRESS',
                replacementHTML: addressParts.join(', ')
              });
              console.log('FormBuilderV2: Added COMPANY_ADDRESS tag to replacements:', addressParts.join(', '));
            }

            // Logo URL is now handled through tag replacements, no need to save to SupplierData

            // Apply the AI content to the preview using the new dict.js approach
            console.log('FormBuilderV2: Applying AI content to preview using dict.js...');
            
            // Get the stored dict content from IndexedDB
            const storedDict = await this.indexedDBService.getDictContent(this.companyGuid || '');
            if (storedDict) {
              // Apply dict content using the new method
              return this.previewService.applyDictContent(storedDict);
            } else {
              // Fallback to old method if no dict content
              console.log('FormBuilderV2: No dict content found, using fallback method...');
              // return this.previewService.applyAIContent(tagReplacements, this.companyGuid || undefined);
            }
          } else {
            console.log('FormBuilderV2: No content changes detected');
            this.isGeneratingContent = false;
            return Promise.resolve();
          }
        })
        .then(async() => {
          console.log('FormBuilderV2: AI content generation completed successfully');

           // �� NEW: Restore saved data from IndexedDB AFTER AI content is applied
          // await this.restoreSavedDataFromIndexedDB();

          // Set AI content generated flag
          await this.formBuilderV2Service.setAIContentGenerated(true);
          console.log('FormBuilderV2: Set AI content generated flag to true');
          
          // NEW: Mark content as generated (NOT section as completed)
          this.isCapabilitiesGenerated = true;
          this.markCapabilitiesGenerationAsUpToDate();
          await this.formBuilderV2Service.updateContentGeneration('capabilities', true);
          console.log('FormBuilderV2: Set capabilities content generation flag to true');
          
          this.updatePreviewForSection('capabilities');
          
          // Show tutorial popup after successful capabilities content generation
          this.showCapabilitiesTutorial();
          
          this.isGeneratingContent = false;
          // OPTIMIZATION: Remove timeout since AI content is already applied to preview

          if (this.shouldCompleteCapabilitiesAfterGeneration) {
            this.shouldCompleteCapabilitiesAfterGeneration = false;
            await this.onCompleteCapabilities();
          }
          
        })
        .catch((error: any) => {
          console.error('FormBuilderV2: Error during AI content generation:', error);
          this.isGeneratingContent = false;
          this.shouldCompleteCapabilitiesAfterGeneration = false;

          this.toastr.error(`Error generating Capabilities content`, 'AI Processing Error');
          
          // On error, stay on capabilities page for user to retry
          setTimeout(() => {
            this.updatePreviewForSection('capabilities');
          }, 100);
        });
    });
  }

  // Update preview with generated content
  updatePreviewWithGeneratedContent(content: any): void {
    console.log('Updating preview with generated content:', content);
    
    // Update the preview service with the generated content
    // This will be implemented based on how the preview service handles content updates
    if (this.previewService) {
      // Update the preview with the new content
      this.previewService.updatePreview(
        this.getCurrentSupplierData(),
        this.formData.template,
        this.formData.theme,
        this.companyGuid || undefined,
        this.editMode
      );
    }
  }

  // Public method to check if AI content has been generated
  hasAIContentBeenGenerated(): boolean {
    return this.formBuilderV2Service.hasAIContentBeenGenerated();
  }

  // Public method to check if all sections are completed
  isAllSectionsCompleted(): boolean {
    return this.isThemeCompleted && 
           this.isCapabilitiesCompleted && 
           this.isAboutCompleted && 
           this.isContactCompleted && 
           this.isLogoCompleted;
  }

  // Handle preview loading state changes
  onPreviewLoadingStateChanged(isLoading: boolean): void {
    this.isPreviewLoading = isLoading;
  }

  async onCompleteCapabilities(): Promise<void> {
    // This should only be called when user clicks "Complete Capabilities" button
    // and all requirements are met (content generated + images uploaded)
    const shouldTriggerCapabilitiesGeneration =
      !this.hasGenerateCapabilitiesBeenClickedSinceSelectionChange ||
      this.hasCapabilitiesSelectionChangedSinceGeneration ||
      !this.isCapabilitiesGenerated;

    if (shouldTriggerCapabilitiesGeneration) {
      if (this.isGeneratingContent) {
        console.log('FormBuilderV2: Capabilities generation already in progress');
        return;
      }

      this.shouldCompleteCapabilitiesAfterGeneration = true;
      this.onGenerateCapabilitiesContent();
      return;
    }
    
    if (!this.isCapabilitiesGenerated) {
      console.warn('Cannot complete Capabilities section: content not generated');
      return;
    }
    
    if (!this.isSectionImagesComplete('capabilities')) {
      console.warn('Cannot complete Capabilities section: images not uploaded');
      return;
    }
    
    this.isCapabilitiesCompleting = true;
    
    // Mark section as completed (unlocks next section)
    this.isCapabilitiesCompleted = true;
    this.saveSectionCompletion('isCapabilitiesCompleted', true);
    
    // Save completion state to IndexedDB
    // this.saveSectionCompletion('isCapabilitiesCompleted', true);
    this.saveCurrentSectionState();
    
    // Update website progress
    this.updateWebsiteProgress('contact');
    
    // ✅ CRITICAL FIX: Update edit mode based on current section
    this.updateEditModeForCurrentSection();
    await this.updateSupplierData();
    
    // Get the next section to open automatically
    const nextSection = this.getNextSection('capabilities');
    if (nextSection) {
      // OPTIMIZATION: Use openSection method which handles both currentActiveSection and updatePreviewForSection
      this.openSection(nextSection as 'template' | 'capabilities' | 'about-us' | 'contact' | 'home' | 'logo');
    } else {
      // All sections completed
      this.currentActiveSection = nextSection;
      this.saveCurrentSectionState();
      this.updatePreviewForSection('capabilities');
    }
    this.isCapabilitiesCompleting = false;
  }

  // About Us methods
  onAboutUsChange(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.formData.aboutUs = target.value;
    console.log('About Us updated:', this.formData.aboutUs);
    
    // Update service
    this.formBuilderV2Service.updateAboutUs(target.value);
    
    // Check if generate button should be enabled
    this.checkGenerateAboutUsButtonState();
  }

  onGenerateAboutUsContent(): void {
    if (this.isGeneratingAboutUsContent) {
      return;
    }
    
    this.isGeneratingAboutUsContent = true;

    // Prepare supplier data and get existing content context for focused about us generation
    Promise.all([
      this.getExistingCompanyName(),
      this.getCurrentLogoURL(),
      this.indexedDBService.getTagReplacements(this.companyGuid || '') // Get existing content for context
    ]).then(async ([existingCompanyName, currentLogoURL, existingTags]) => {
      
      // Prepare supplier data for focused about us generation
      const supplierData = {
        companyName: existingCompanyName,
        contactEmail: this.formData.contact.email,
        phoneNumber: this.formData.contact.phone,
        streetAddress: this.formData.contact.streetAddress,
        streetAddress2: '', // Required by SupplierData interface
        city: this.formData.contact.city,
        countryRegion: this.formData.contact.state,
        postalCode: this.formData.contact.zipCode,
        aboutContent: this.formData.aboutUs, // Include existing About Us content if any
        capabilities: this.formData.capabilities.join(', '),
        equipment: this.formData.equipment,
        other_capabilities: this.formData.additionalCapabilities,
        websiteURL: '',
        logoURL: currentLogoURL
      };

      // Convert existing tags to content context object for AI crew
      const existingContentContext = (existingTags || []).reduce((acc: Record<string, string>, tag: any) => {
        acc[tag.tagName] = tag.replacementHTML;
        return acc;
      }, {} as Record<string, string>);

      try {
        // Call focused about us generation - generates only the 4 core about us tags
        const aboutUsTagReplacements = await this.aiContentService.generateAboutUsContent(
          supplierData,
          existingContentContext
        );

        if (aboutUsTagReplacements.length > 0) {
          const contentAlreadyExists = await this.indexedDBService.getDictContent(this.companyGuid || '');
          if(!contentAlreadyExists){
            await this.indexedDBService.storeDictContent(this.companyGuid || '', {
              "CompanyInfo": {
                  "COMPANY_NAME": "Lorem Ipsum Company",
                  "COMPANY_EMAIL": "info@loremipsum.com",
                  "COMPANY_PHONE": "(555) 123-4567",
                  "COMPANY_ADDRESS": "123 Lorem Ipsum Drive, Dolor Sit, CA 90210",
                  "COMPANY_COPYRIGHT": "© 2026 Lorem Ipsum Company. All Rights Reserved."
              },
              "HeroSection": {
                  "HERO_MAIN_TITLE": "Lorem Ipsum Dolor Sit Amet",
                  "HERO_SUBTITLE": "Consectetur Adipiscing",
                  "HERO_DESCRIPTION": "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat."
              },
              "AboutSection": {
                  "ABOUT_MAIN_HEADING": "Lorem Ipsum Dolor Sit",
                  "ABOUT_DESCRIPTION_1": "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
                  "ABOUT_DESCRIPTION_2": "Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.",
                  "ABOUT_DESCRIPTION_3": "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat."
              },
              "CapabilitiesSection": {
                  "CAPABILITIES_MAIN_HEADING": "Lorem Ipsum Capabilities",
                  "CAPABILITIES_DESCRIPTION": "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris.",
                  "services": [
                      {
                          "SERVICE_1_TITLE": "Lorem Ipsum Service",
                          "SERVICE_1_DESCRIPTION": "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua."
                      },
                      {
                          "SERVICE_2_TITLE": "Dolor Sit Amet",
                          "SERVICE_2_DESCRIPTION": "Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat."
                      },
                      {
                          "SERVICE_3_TITLE": "Consectetur Adipiscing",
                          "SERVICE_3_DESCRIPTION": "Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur."
                      }
                  ]
              },
              "EquipmentSection": {
                  "EQUIPMENT_OVERVIEW_DESCRIPTION": "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation.",
                  "equipmentItems": [
                      {
                          "EQUIPMENT_1_TITLE": "Lorem Ipsum Equipment",
                          "EQUIPMENT_1_DESCRIPTION": "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua."
                      },
                      {
                          "EQUIPMENT_2_TITLE": "Dolor Sit Machinery",
                          "EQUIPMENT_2_DESCRIPTION": "Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat."
                      },
                      {
                          "EQUIPMENT_3_TITLE": "Amet Consectetur Tools",
                          "EQUIPMENT_3_DESCRIPTION": "Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur."
                      }
                  ]
              },
              "AdditionalServicesSection": {
                  "ADDITIONAL_SERVICES_HEADING": "Lorem Ipsum Services",
                  "ADDITIONAL_SERVICES_DESCRIPTION": "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
                "additionalServices": [
                      {
                          "ADDITIONAL_SERVICE_1_TITLE": "Lorem Ipsum Consultation",
                          "ADDITIONAL_SERVICE_1_DESCRIPTION": "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua."
                      },
                      {
                          "ADDITIONAL_SERVICE_2_TITLE": "Dolor Sit Sourcing",
                          "ADDITIONAL_SERVICE_2_DESCRIPTION": "Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat."
                      },
                      {
                          "ADDITIONAL_SERVICE_3_TITLE": "Amet Consectetur Support",
                          "ADDITIONAL_SERVICE_3_DESCRIPTION": "Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur."
                      }
                  ]
              },
              "ContactSection": {
                  "CONTACT_MAIN_HEADING": "Lorem Ipsum Contact",
                  "CONTACT_DESCRIPTION": "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam."
              },
              "FooterSection": {
                  "FOOTER_DESCRIPTION": "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam."
              }
            });
          }

          // Use the same infrastructure as inline content editing
        // Update each tag individually in dictContent using the existing flow
        for (const replacement of aboutUsTagReplacements) {
          await this.previewService.updateSingleTagInDict(
            replacement.tagName,
            replacement.replacementHTML
          );
        }

          
          // Use existing infrastructure to apply the focused about us updates
          // This will update only the 4 about us tags while preserving all capabilities/equipment/service tags
          // await this.previewService.applyAIContent(aboutUsTagReplacements, this.companyGuid || undefined);
          
          // NEW: Mark content as generated (NOT section as completed)
          this.isAboutGenerated = true;
          await this.formBuilderV2Service.updateContentGeneration('about', true);
          
          // Navigate to about page to show the generated content
          this.updatePreviewForSection('about-us');
          
          // Show tutorial popup after successful About Us content generation
          this.showAboutUsTutorial();
          
          } else {
          
          // Check if this was due to an error (empty array from AI service)
          // The AI service returns empty array on error, so we should show error message
          this.toastr.error('Failed to generate About Us content. Please try again.', 'AI Processing Error');
          
          // Even if no new content generated, mark content as generated if user tried to generate
          this.isAboutGenerated = true;
          await this.formBuilderV2Service.updateContentGeneration('about', true);
          
          this.updatePreviewForSection('about-us');
         
        }
        
      } catch (error) {
        
        // On error, existing content is preserved (no changes made)
        // Still navigate to about page for user to retry
        this.updatePreviewForSection('about-us');
        
      } finally {
        this.isGeneratingAboutUsContent = false;
        this.updateSupplierData();
      }
    }).catch((error) => {
          this.isGeneratingAboutUsContent = false;
          this.toastr.error(`Failed to prepare About Us generation`, 'Setup Error');
          
      // On preparation error, stay on about page for user to retry
          setTimeout(() => {
            this.updatePreviewForSection('about-us');
          }, 100);
    });
  }

  async onCompleteAboutUs(): Promise<void> {
    // This should only be called when user clicks "Complete About Us" button
    // and all requirements are met (content generated + images uploaded)
    
    if (!this.isAboutGenerated) {
      console.warn('Cannot complete About Us section: content not generated');
      return;
    }
    
    if (!this.isSectionImagesComplete('about-us')) {
      console.warn('Cannot complete About Us section: images not uploaded');
      return;
    }
    
    this.isAboutCompleting = true;
    
    // Mark section as completed (unlocks next section)
    this.isAboutCompleted = true;
    this.saveSectionCompletion('isAboutCompleted', true);
    
    // Save completion state to IndexedDB
    // this.saveSectionCompletion('isAboutCompleted', true);
    
    // Update website progress
    this.updateWebsiteProgress('capabilities');
    
    // ✅ CRITICAL FIX: Update edit mode based on current section
    this.updateEditModeForCurrentSection();
    await this.updateSupplierData();
    
    // Get the next section to open automatically
    const nextSection = this.getNextSection('about-us');
    
    if (nextSection) {
      // OPTIMIZATION: Use openSection method which handles both currentActiveSection and updatePreviewForSection
      this.openSection(nextSection as 'template' | 'capabilities' | 'about-us' | 'contact' | 'home' | 'logo');
    } else {
      // All sections completed
      this.currentActiveSection = nextSection;
      this.saveCurrentSectionState();
      this.updatePreviewForSection('about-us');
    }
    this.isAboutCompleting = false;
  }

  // Contact methods
  onContactChange(field: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    
    // Update form data
    this.formData.contact[field as keyof typeof this.formData.contact] = value;
    
    
    // Update service
    this.formBuilderV2Service.updateContact(this.formData.contact);

    // Save contact data to dict content for immediate preview update
    // this.saveContactDataToDictContent();
  }

  // Handle contact field changes with validation error clearing
  onContactFieldChange(field: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    
    // Special handling for phone field - only allow digits and limit to 10
    if (field === 'phone') {
      // Remove all non-digit characters and limit to 10 digits
      const phoneDigits = target.value.replace(/\D/g, '').substring(0, 10);
      target.value = phoneDigits;
      
      // Update the form data with the cleaned value
      this.formData.contact.phone = phoneDigits;
    }
    
    // Clear error state when user starts typing
    if (this.contactFieldErrors[field] && target.value.trim()) {
      this.contactFieldErrors[field] = false;
    }
    
    // Call existing method
    this.onContactChange(field, event);
  }


  async onCompleteContact(): Promise<void> {
    // Validate all contact fields first
    if (!this.validateContactFields()) {
      return;
    }
    
    this.isContactCompleting = true;
    this.isContactCompleted = true;
    
    // Save completion state to IndexedDB
    this.saveSectionCompletion('isContactCompleted', true);
    
    // Update website progress
    this.updateWebsiteProgress('logo');
    
    // ✅ CRITICAL FIX: Update edit mode based on current section
    this.updateEditModeForCurrentSection();
    
    // Ensure contact data is saved to dictContent before completing
    this.saveContactDataToDictContent().then(() => {
      const nextSection = this.getNextSection('contact');
      this.updateSupplierData();
      
      if (nextSection) {
        // OPTIMIZATION: Use openSection method which handles currentActiveSection, updatePreviewForSection, and logo special handling
        this.openSection(nextSection as 'template' | 'capabilities' | 'about-us' | 'contact' | 'home' | 'logo');
      } else {
        // All sections completed
        this.currentActiveSection = nextSection;
        this.saveCurrentSectionState();
        this.updatePreviewForSection('contact');
      }
    });
    this.isContactCompleting = false;
  }

  // File upload methods
  onFileSelect(event: Event, labelId: string): void {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (file && isPlatformBrowser(this.platformId)) {
      const label = document.getElementById(labelId);
      if (label) {
        const displayName = file.name.length > 20 ? file.name.substring(0, 20) + '...' : file.name;
        label.textContent = displayName;
      }
      console.log('File selected:', file.name);
    }
  }

  // File input click handlers
  onImage1Click(): void {
    if (isPlatformBrowser(this.platformId)) {
      document.getElementById('image1')?.click();
    }
  }

  onImage2Click(): void {
    if (isPlatformBrowser(this.platformId)) {
      document.getElementById('image2')?.click();
    }
  }

  onImage3Click(): void {
    if (isPlatformBrowser(this.platformId)) {
      document.getElementById('image3')?.click();
    }
  }

  onLogoClick(): void {
    if (isPlatformBrowser(this.platformId)) {
      document.getElementById('logo-upload')?.click();
    }
  }

  // Action buttons
  async onSaveForLater(): Promise<void> {
    if (!this.companyGuid) {
      console.error('Cannot save for later: company GUID is missing');
      return;
    }

    if (this.isSavingForLater) {
      console.log('Save for later already in progress...');
      this.toastr.info('Save operation already in progress...', 'Please Wait');
      return;
    }

    console.log('🔄 Starting save for later process...');
    this.isSavingForLater = true;
    this.mixPanelService.trackSaveForLater();
    this.updateSupplierData();
      // try {
      //   const result = await this.supplierSiteDataService.saveSupplierSiteData(this.companyGuid);

      //   if (result.success) {
      //     console.log('✅ Save for later completed successfully');
      //   } else {
      //     console.error('❌ Save for later failed:', result.message);
      //   }
      // } catch (error) {
      //   console.error('❌ Save for later error:', error);
      // } finally {
      //   this.isSavingForLater = false;
      //   this.cdr.detectChanges();
      // }
    this.isSavingForLater = false;
    this.toastr.success('Data has been saved', 'Success');
    this.cdr.detectChanges();
  }

  async onPublishSite(): Promise<void> {
    // Track website deployment
    if(this.editMode){
      if(!this.isSectionImagesComplete('about-us')){
        this.openSection('about-us');
        this.currentActiveSection = 'about-us';
        this.toastr.warning('Please complete the About Us section images before publishing', 'Incomplete Section');
        return;
      }
      else if(!this.isSectionImagesComplete('capabilities')){
        this.openSection('capabilities');
        this.currentActiveSection = 'capabilities';
        this.toastr.warning('Please complete the Capabilities section images before publishing', 'Incomplete Section');
        return;
      }
      else if(!this.isSectionImagesComplete('logo')){
        this.openSection('logo');
        this.currentActiveSection = 'logo';
        this.toastr.warning('Please complete the Logo section before publishing', 'Incomplete Section');
        return;
      }
    }
    this.mixPanelService.trackDeployment();

    try {
      // Set button to loading state (don't show publishing overlay yet)
      this.isPublishButtonLoading = true;
      
      // Prepare files and get deploymentId first
      await this.prepareAndDeployFiles();
      
    } catch (error) {
      console.error('FormBuilderV2: Deployment error:', error);
      this.toastr.error('Website deployment failed. Please try again.', 'Deployment Error');
      this.handleDeploymentError(`Deployment failed: ${error}`);
      this.isPublishButtonLoading = false;
    }
  }

  /**
   * Prepare files and deploy to get deploymentId before showing publishing overlay
   */
  private async prepareAndDeployFiles(): Promise<void> {
    try {
      // Clear existing deploymentId from IndexedDB to ensure consistent deployment and polling
      console.log('FormBuilderV2: Clearing existing deploymentId from IndexedDB...');
      await this.formBuilderV2Service.updateDeploymentId('');
      console.log('✅ FormBuilderV2: Existing deploymentId cleared successfully');
      
      // WYSIWYG SYNC: Ensure preview content matches deployment content
      console.log('FormBuilderV2: Syncing preview state to deployment files...');
      try {
        await this.previewService.updatePreview(this.getCurrentSupplierData(), this.formData.template, this.formData.theme, this.companyGuid || undefined, this.editMode);
        await this.previewService.syncPreviewToDeploymentFiles();
        console.log('✅ FormBuilderV2: WYSIWYG sync completed successfully');
      } catch (syncError) {
        console.error('❌ FormBuilderV2: WYSIWYG sync failed, proceeding with deployment anyway:', syncError);
        // Continue with deployment - sync failure should not block deployment
      }
      
      // NEW: Process files with content-manager replication before deployment
      console.log('FormBuilderV2: Processing files with content-manager replication...');
      try {
        await this.processFilesForDeployment();
        console.log('✅ FormBuilderV2: Content-manager replication completed successfully');
      } catch (processError) {
        console.error('❌ FormBuilderV2: Content-manager replication failed, proceeding with deployment anyway:', processError);
        // Continue with deployment - processing failure should not block deployment
      }
      
      // Get deployment zip and file count
      const websiteZip = await this.previewService.getDeploymentZip();
      const fileCount = this.previewService.getFileCount();

      // Generate folder name from company name
      const supplierData = this.getCurrentSupplierData();
      const folderName = this.sanitizeFolderName(supplierData.companyName);
      
      let companyGuidFromStorage = '';
      if (isPlatformBrowser(this.platformId)) {
        companyGuidFromStorage = localStorage.getItem('company_guid') || '';
        console.log('FormBuilderV2: Retrieved company_guid from localStorage:', companyGuidFromStorage);
      }
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('website_zip', websiteZip, 'website.zip');
      formData.append('folder_name', folderName);
      formData.append('company_id', this.companyGuid || '');
      formData.append('company_guid', companyGuidFromStorage);
      
      // Deploy using existing service
      const deployResult: any = await firstValueFrom(
        this.websiteGenerationService.deployWebsiteFiles(formData)
      );
      
      if (deployResult.success) {
        // Save deploymentId to IndexedDB
        await this.formBuilderV2Service.updateDeploymentId(deployResult.deploymentId);
        
        
        // Now show the publishing overlay
        this.showPublishingOverlay(deployResult.deploymentId);
      } else {
        throw new Error(deployResult.message || 'Deployment failed');
      }
      
    } catch (error) {
      console.error('FormBuilderV2: Error in prepareAndDeployFiles:', error);
      throw error;
    }
  }

  /**
   * Show the publishing overlay after deploymentId is received
   */
  private async showPublishingOverlay(deploymentId: string): Promise<void> {
    // Set all the deployment states
    this.previewService.setEditMode(false, this.companyGuid || undefined);
    this.previewService.loadTemplatePage('index', this.getCurrentSupplierData(), this.companyGuid || undefined);
    
    // Set deployment state and persist to IndexedDB
    this.formBuilderV2Service.updateFormData({ isDeploying: true });
    this.isDeploying = true;
    this.showDeploymentFinalMessage = false;
    this.deploymentFinalMessage = '';
    this.deploymentStatus = DeploymentProgressMessages.SETTING_UP_DOMAIN;
    this.deploymentProgress = 0;
    this.deploymentAttempts = 0;
    this.deploymentIconStatus = 'loading';

    if (this.companyGuid) {
      await this.supplierSiteDataService.saveSupplierSiteData(this.companyGuid);
    }
    
    // Clear the publish button loading state
    this.isPublishButtonLoading = false;
    
    // Force change detection to ensure UI updates immediately
    this.cdr.detectChanges();

    // Send data to DynamoDB
    this.sendDataToDynamo();
    
    // Check if deployment has already completed (for early closure scenarios)
    this.checkDeploymentCompletion();
    
    // Start polling for deployment status
    this.pollDeploymentStatus(deploymentId);
  }

  /**
   * Return to edit mode from publishing/deployment state
   * This function reverses the showPublishingOverlay state and returns to editing
   */
  private async backToEditMode(): Promise<void> {
    // Set all the deployment states back to edit mode
    this.previewService.setEditMode(true, this.companyGuid || undefined);
    this.previewService.loadTemplatePage('index', this.getCurrentSupplierData(), this.companyGuid || undefined);
    
    // Reset deployment state and persist to IndexedDB
    this.formBuilderV2Service.updateFormData({ isDeploying: false });
    this.isDeploying = false;
    this.showDeploymentFinalMessage = false;
    this.deploymentFinalMessage = '';
    this.deploymentStatus = '';
    this.deploymentProgress = 0;
    this.deploymentAttempts = 0;
    this.deploymentIconStatus = 'loading';
    this.deploymentWebsiteUrl = '';

    if (this.companyGuid) {
      await this.supplierSiteDataService.saveSupplierSiteData(this.companyGuid);
    }
    
    // Clear the publish button loading state
    this.isPublishButtonLoading = false;
    
    // Force change detection to ensure UI updates immediately
    this.cdr.detectChanges();

    // Update edit mode based on current section
    this.updateEditModeForCurrentSection();
  }

  /**
   * Handle back to edit mode from site-publishing component
   */
  async onBackToEditMode(): Promise<void> {
    try {
      // Show loading state to user
      console.log('FormBuilderV2: Switching to edit mode...');
      
      // Call the existing backToEditMode function to maintain current functionality
      await this.backToEditMode();
      
      // 🆕 ADD QUERY PARAMETER AND REFRESH
      if(!this.editMode){
        await this.addEditModeQueryParamAndRefresh();
      }
      
    } catch (error) {
      console.error('FormBuilderV2: Error switching to edit mode:', error);
    }
  }

  /**
   * Add editMode=true query parameter and refresh the page
   */
  private async addEditModeQueryParamAndRefresh(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return; // Skip on server-side rendering
    }

    try {
      // Get current URL
      const currentUrl = new URL(window.location.href);
      
      // Check if editMode query parameter already exists
      const existingEditMode = currentUrl.searchParams.get('ed');
      
      // Only add the parameter if it doesn't exist or is not 'true'
      if (existingEditMode !== 'true') {
        // Add editMode=true query parameter
        currentUrl.searchParams.set('ed', 'dHJ1ZQ==');
        
        // Update the URL in browser history
        window.history.replaceState({}, '', currentUrl.toString());
        
        console.log('FormBuilderV2: Added editMode=true to URL:', currentUrl.toString());
      } else {
        console.log('FormBuilderV2: editMode=true already exists in URL');
      }
      
      // Small delay to ensure state is saved before refresh
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Refresh the page to ensure clean state
      console.log('FormBuilderV2: Refreshing page to apply edit mode...');
      window.location.reload();
      
    } catch (error) {
      console.error('FormBuilderV2: Error adding edit mode query parameter:', error);
      // Fallback: just refresh the page
      window.location.reload();
    }
  }

  // Utility methods
  updateCharCount(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    const charCount = textarea.value.length;
    const maxLength = textarea.getAttribute('maxlength');
    const charCountElement = textarea.parentElement?.querySelector('.char-count');
    if (charCountElement && maxLength) {
      charCountElement.textContent = `${charCount}/${maxLength}`;

      // Change color if approaching limit
      if (charCount > parseInt(maxLength) * 0.9) {
        (charCountElement as HTMLElement).style.color = '#e74c3c';
      } else if (charCount > parseInt(maxLength) * 0.8) {
        (charCountElement as HTMLElement).style.color = '#f39c12';
      } else {
        (charCountElement as HTMLElement).style.color = '#999';
      }
    }
  }

  // Accordion functionality
  toggleAccordion(event: Event): void {
    if (!isPlatformBrowser(this.platformId)) return;
    
    const header = event.target as HTMLElement;
    const content = header.nextElementSibling;
    if (content) {
      const isActive = content.classList.contains('active');

      // Close all accordions
      document.querySelectorAll('.accordion-content.active').forEach(accordion => {
        accordion.classList.remove('active');
      });

      // Open clicked accordion if it wasn't already open
      if (!isActive) {
        content.classList.add('active');
      }
    }
  }

  onStartNowClick(event: Event, section: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    
    // Prevent event propagation to avoid triggering the header click
    event.stopPropagation();
    
    // Find the accordion content for this section and open it
    const button = event.target as HTMLElement;
    const header = button.closest('.section-header');
    const content = header?.nextElementSibling;
    
    if (content) {
      // Close all accordions first
      document.querySelectorAll('.accordion-content.active').forEach(accordion => {
        accordion.classList.remove('active');
      });

      // Open this accordion
      content.classList.add('active');
      
      console.log(`Started working on section: ${section}`);
    }
  }

  // Preview panel update
  private updatePreviewWithFormData(): void {
    // OPTIMIZATION: Add update guard to prevent unnecessary updates
    if (this.isPreviewLoading) {
      return;
    }

    // Convert V2 form data to V1 supplier data format for preview
    this.getExistingCompanyName().then(existingCompanyName => {
      const supplierData = {
        companyName: existingCompanyName || '', // Use existing company name from DynamoDB
        contactEmail: this.formData.contact.email,
        phoneNumber: this.formData.contact.phone,
        streetAddress: this.formData.contact.streetAddress,
        city: this.formData.contact.city,
        countryRegion: this.formData.contact.state,
        postalCode: this.formData.contact.zipCode,
        aboutContent: this.formData.aboutUs,
        capabilities: this.formData.capabilities.join(', '),
        equipment: this.formData.equipment,
        websiteURL: '',
        streetAddress2: '',
        logoURL: ''
      };
      
      // Update preview with current form data
      this.previewService.updatePreview(
        supplierData, 
        this.formData.template, 
        this.formData.theme, 
        this.companyGuid || undefined,
        this.editMode
      );
    });
  }

  /**
   * CRITICAL FIX: Ensure preview is properly loaded on first page load
   * This method fixes the "Loading preview..." issue by ensuring the preview update is triggered
   */
  private async ensurePreviewIsLoaded(): Promise<void> {
    try {
      console.log('FormBuilderV2: Ensuring preview is properly loaded...');
      
      // First, update preview with current form data
      this.updatePreviewWithFormData();
      
      // Then, ensure the template page is loaded for the current section
      this.updatePreviewForSection('template');
      
      // Add a small delay to ensure all async operations complete
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Force a final preview update to ensure it's displayed
      const supplierData = this.getCurrentSupplierData();
      await this.previewService.updatePreview(
        supplierData, 
        this.formData.template, 
        this.formData.theme, 
        this.companyGuid || undefined,
        this.editMode
      );
      
      console.log('FormBuilderV2: Preview loading sequence completed');
    } catch (error) {
      console.error('FormBuilderV2: Error ensuring preview is loaded:', error);
      // Fallback: try updating preview with basic data
      this.updatePreviewWithFormData();
    }
  }


 

  // Note: Logo editing now uses standard image editing flow like other sections

  /**
   * Map API response to SupplierData interface (moved from homepage component)
   */
  private mapApiToSupplierData(metadata: any, contact: any): SupplierData {
    return {
      companyName: metadata?.company_name || '',
      phoneNumber: this.websiteGenerationService.formatPhoneNumber(contact?.phone) || '',
      city: contact?.city || '',
      countryRegion: contact?.country || '',
      websiteURL: '', // Not available in metadata/contact
      streetAddress: contact?.address1 || '',
      streetAddress2: contact?.address2 || contact?.state || '',
      logoURL: '', // Logo URL is now handled through tag replacements
      contactEmail: contact?.email || '',
      postalCode: contact?.zipcode || '',
      equipment: Array.isArray(metadata?.my_equipment) ? metadata.my_equipment.join(', ') : '',
      capabilities: Array.isArray(metadata?.my_capabilities) ? metadata.my_capabilities.join(', ') : '',
      aboutContent: '', // Not available in metadata/contact
    };
  }

  // Section persistence helper methods
  private async saveCurrentSectionState(): Promise<void> {
    try {
      await this.formBuilderV2Service.updateCurrentActiveSection(this.currentActiveSection);
      console.log('FormBuilderV2: Current section state saved:', this.currentActiveSection);
    } catch (error) {
      console.error('FormBuilderV2: Error saving current section state:', error);
    }
  }

  private async loadCurrentSectionState(): Promise<void> {
    try {
      const savedSection = this.formBuilderV2Service.getCurrentActiveSection();
      if (savedSection) {
        this.currentActiveSection = savedSection;
      } else {
        this.currentActiveSection = 'template'; // fallback to template
        // 🔧 FIX: Persist the fallback value immediately to sync state
        await this.saveCurrentSectionState();
        console.log('FormBuilderV2: Persisted fallback section state to IndexedDB');
      }
    } catch (error) {
      this.currentActiveSection = 'template'; // fallback on error
      // 🔧 FIX: Persist fallback on error too
      await this.saveCurrentSectionState();
      console.log('FormBuilderV2: Persisted fallback section state after error');
    }
  }

  // Section completion state helper methods
  private async saveSectionCompletion(section: keyof SectionCompletionStates, isCompleted: boolean): Promise<void> {
    try {
      await this.formBuilderV2Service.updateSectionCompletion(section, isCompleted);
      console.log(`FormBuilderV2: Section completion saved - ${section}: ${isCompleted}`);
      
      // Track section completion
      if (isCompleted) {
        console.log('Tracking section completion:', section);
        const sectionName = section.replace('is', '').replace('Completed', '');
        this.mixPanelService.trackSectionCompletion(sectionName);
      }
    } catch (error) {
      console.error(`FormBuilderV2: Error saving section completion for ${section}:`, error);
    }
  }

  private async loadSectionCompletionStates(): Promise<void> {
    try {
      const savedStates = this.formBuilderV2Service.getAllSectionCompletions();
      
      // Sync component state with saved completion states
      this.isHomeCompleted = savedStates.isHomeCompleted;
      this.isThemeCompleted = savedStates.isThemeCompleted;
      this.isCapabilitiesCompleted = savedStates.isCapabilitiesCompleted;
      this.isAboutCompleted = savedStates.isAboutCompleted;
      this.isContactCompleted = savedStates.isContactCompleted;
      this.isLogoCompleted = savedStates.isLogoCompleted;
      
      // NEW: Restore generation states from form data
      const generationStates = this.formBuilderV2Service.getGenerationStates();
      this.isAboutGenerated = generationStates.isAboutGenerated;
      this.isCapabilitiesGenerated = generationStates.isCapabilitiesGenerated;
      this.lastGeneratedCapabilities = this.isCapabilitiesGenerated ? [...this.formData.capabilities] : [];
      this.hasCapabilitiesSelectionChangedSinceGeneration = false;
      this.hasGenerateCapabilitiesBeenClickedSinceSelectionChange = this.isCapabilitiesGenerated;
      
      console.log('FormBuilderV2: Loaded section completion states:', savedStates);
      console.log('FormBuilderV2: Loaded generation states - About:', this.isAboutGenerated, 'Capabilities:', this.isCapabilitiesGenerated);
    } catch (error) {
      console.error('FormBuilderV2: Error loading section completion states:', error);
      // Fallback to default states (all false) - they're already initialized in the component
    }
  }

  // Smart completion state detection based on existing data
  private async detectCompletionStatesFromData(): Promise<void> {
    try {
      let hasChanges = false;
      const currentStates = this.formBuilderV2Service.getAllSectionCompletions();

      // Auto-detect theme completion based on template and theme selection
      if (!currentStates.isThemeCompleted && this.formData.template && this.formData.theme) {
        this.isThemeCompleted = true;
        await this.saveSectionCompletion('isThemeCompleted', true);
        hasChanges = true;
        console.log('FormBuilderV2: Auto-detected theme completion based on data');
      }

      // Auto-detect capabilities completion based on AI content generation
      if (!currentStates.isCapabilitiesCompleted && this.previewService.hasAIContentBeenGenerated()) {
    this.isCapabilitiesCompleted = true;
        await this.saveSectionCompletion('isCapabilitiesCompleted', true);
        hasChanges = true;
        console.log('FormBuilderV2: Auto-detected capabilities completion based on AI content');
      }

      // Auto-detect about completion based on form data
      if (!currentStates.isAboutCompleted && this.formData.aboutUs && this.formData.aboutUs.trim().length > 0) {
        this.isAboutCompleted = true;
        await this.saveSectionCompletion('isAboutCompleted', true);
        hasChanges = true;
        console.log('FormBuilderV2: Auto-detected about completion based on data');
      }

      // Auto-detect contact completion based on required contact fields
      if (!currentStates.isContactCompleted && this.hasRequiredContactInfo()) {
        this.isContactCompleted = true;
        await this.saveSectionCompletion('isContactCompleted', true);
        hasChanges = true;
        console.log('FormBuilderV2: Auto-detected contact completion based on data');
      }
      // Auto-detect logo completion based on logo existence in tag replacements
      if (!currentStates.isLogoCompleted && this.companyGuid) {
        const storedTagReplacements = await this.indexedDBService.getTagReplacements(this.companyGuid);
        const logoReplacement = storedTagReplacements?.find(replacement => replacement.tagName === 'COMPANY_LOGO');
        if (logoReplacement?.replacementHTML && logoReplacement.replacementHTML.trim().length > 0) {
    this.isLogoCompleted = true;
          await this.saveSectionCompletion('isLogoCompleted', true);
          hasChanges = true;
          console.log('FormBuilderV2: Auto-detected logo completion based on tag replacement data');
        }
      }

      if (hasChanges) {
        console.log('FormBuilderV2: Smart detection completed - some states were auto-detected');
      }
    } catch (error) {
      console.error('FormBuilderV2: Error during smart completion state detection:', error);
    }
  }

  // Helper method to check if required contact info is present
  private hasRequiredContactInfo(): boolean {
    const contact = this.formData.contact;
    return !!(
      contact.email && contact.email.trim() &&
      contact.phone && contact.phone.trim() &&
      contact.streetAddress && contact.streetAddress.trim() &&
      contact.city && contact.city.trim() &&
      contact.state && contact.state.trim() &&
      contact.zipCode && contact.zipCode.trim()
    );
  }

  // Validate contact fields and set error states
  private validateContactFields(): boolean {
    const contact = this.formData.contact;
    const requiredFields = ['email', 'phone', 'streetAddress', 'city', 'state', 'zipCode'];
    
    // Reset all errors
    this.contactFieldErrors = {};
    
    let isValid = true;
    
    requiredFields.forEach(field => {
      const value = contact[field as keyof typeof contact];
      const isEmpty = !value || value.trim() === '';
      
      if (isEmpty) {
        this.contactFieldErrors[field] = true;
        isValid = false;
      } else if (field === 'phone') {
        // Special validation for phone: must be exactly 10 digits
        const phoneDigits = value.replace(/\D/g, ''); // Remove all non-digit characters
        if (phoneDigits.length !== 10) {
          this.contactFieldErrors[field] = true;
          isValid = false;
        }
      }
    });
    
    return isValid;
  }

  private sanitizeFolderName(name: string): string {
    const startsWithNumber = /^\d/.test(name.trim());
    let sanitizedName = name.toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '-')
    .replace(/^-+|-+$/g, '');

    if(startsWithNumber){
      sanitizedName = 'a-' + sanitizedName;
    }
    return sanitizedName;
  }

  private async pollDeploymentStatus(deploymentId: string): Promise<void> {
    const config = environment.deploymentConfig;
    let attempts = 0;
    
    const checkStatus = async () => {
      if (attempts >= config.maxAttempts) {
        
        // If we have a website URL, treat as success instead of timeout
        if (this.potentialWebsiteUrl && this.potentialWebsiteUrl.trim() !== '') {
          this.handleDeploymentSuccess(this.potentialWebsiteUrl);
          return;
        }
        
        this.handleDeploymentTimeout();
        return;
      }
      
      try {
        const status = await firstValueFrom(
          this.websiteGenerationService.getDeploymentStatus(deploymentId)
        );
        
        // Store potential website URL for later use - always capture URL even if popup is closed
        if (status.websiteUrl && status.websiteUrl.trim() !== '') {
          this.potentialWebsiteUrl = status.websiteUrl;
          console.log('FormBuilderV2: Captured potential website URL:', status.websiteUrl);
        }
        
        // Check if deployment is complete - EXACTLY like V1 logic (check BEFORE updating progress)
        if (status.status === DeploymentStatusTypes.DONE) {
          if (status.websiteUrl) {
            this.handleDeploymentSuccess(status.websiteUrl);
          } else {
            this.handleDeploymentSuccess('');
          }
          return; // Stop polling immediately
        } else if (status.status === DeploymentStatusTypes.FAILED) {
          console.log('FormBuilderV2: Deployment status is FAILED, calling error handler');
          this.handleDeploymentError(status.errorDetails || 'Deployment failed');
          return; // Stop polling immediately
        }
        
        // Update progress based on status (only for in-progress statuses)
        this.updateDeploymentProgress(status);
        
        // Continue polling if still in progress
        attempts++;
        this.deploymentAttempts = attempts;
        console.log(`FormBuilderV2: Deployment still in progress (${status.status}), continuing to poll...`);
        setTimeout(checkStatus, config.pollInterval);
        
      } catch (error) {
        console.error('FormBuilderV2: Error checking deployment status:', error);
        this.handleDeploymentError(`Status check failed: ${error}`);
      }
    };
    
    // Start checking status with initial delay
    console.log('FormBuilderV2: Starting deployment status polling...');
    setTimeout(checkStatus, config.pollInterval);
  }

  private updateDeploymentProgress(status: any): void {
    console.log('FormBuilderV2: updateDeploymentProgress called with status:', status.status);
    
    // Update status message based on deployment phase
    switch (status.status) {
      case DeploymentStatusTypes.PROCESSING:
        this.deploymentStatus = DeploymentProgressMessages.SETTING_UP_DOMAIN;
        this.deploymentProgress = 20;
        break;
      case DeploymentStatusTypes.DEPLOYING:
        this.deploymentStatus = DeploymentProgressMessages.UPLOADING_CONTENT;
        this.deploymentProgress = 40;
        break;
      case DeploymentStatusTypes.VERIFYING:
        this.deploymentStatus = DeploymentProgressMessages.CONFIGURING_WEBSITE;
        this.deploymentProgress = 80;
        break;
      case DeploymentStatusTypes.ARGOCD_SYNC:
        this.deploymentStatus = DeploymentProgressMessages.SYNCING_INFRASTRUCTURE;
        this.deploymentProgress = 90;
        break;
      default:
        this.deploymentStatus = DeploymentProgressMessages.SETTING_UP_DOMAIN;
        this.deploymentProgress = 20;
    }
    
    console.log('FormBuilderV2: Progress updated - Status:', this.deploymentStatus, 'Progress:', this.deploymentProgress + '%');
    
    // Force change detection to ensure UI updates immediately
    this.cdr.detectChanges();
  }

  private handleDeploymentSuccess(websiteUrl: string): void {
    console.log('FormBuilderV2: handleDeploymentSuccess called with websiteUrl:', websiteUrl);
    
    // Track successful deployment
    this.mixPanelService.track('Deployment Success', {
      website_url: websiteUrl
    });
    
    // EXACTLY like V1: Immediately stop deployment state and show success message
    // this.isDeploying = false;
    this.deploymentProgress = 100;
    this.deploymentIconStatus = 'success';
        
    // Set final success message with URL if available (like V1's addMessage)
    if (websiteUrl) {
      this.deploymentStatus = `🎉 Your website is now live! Visit: ${websiteUrl}`;
      this.deploymentFinalMessage = `🎉 Your website is now live! You can access it at the URL below:`;
      this.deploymentWebsiteUrl = websiteUrl;
      
    } else {
      this.deploymentStatus = DeploymentSuccessMessages.WEBSITE_READY;
      this.deploymentFinalMessage = '🎉 Your website has been successfully deployed!';
      this.deploymentWebsiteUrl = '';
    }
    
    // Show final message immediately - no delay
    this.showDeploymentFinalMessage = true;


    
    // Force change detection to ensure UI updates immediately
    this.cdr.detectChanges();
    console.log('FormBuilderV2: Change detection triggered');
  }

  private handleDeploymentError(error: string): void {
    // Track deployment error
    this.mixPanelService.track('Deployment Error', {
      error_message: error
    });
    
    // Clear the publish button loading state
    this.isPublishButtonLoading = false;
    
    // this.isDeploying = false;
    this.deploymentStatus = `❌ Deployment failed: ${error}`;
    this.deploymentProgress = 0;
    this.deploymentIconStatus = 'error';
    
    console.error('FormBuilderV2: Deployment failed:', error);
    
    // Set final error message - always include URL if available
    if (this.potentialWebsiteUrl && this.potentialWebsiteUrl.trim() !== '') {
      this.deploymentFinalMessage = `❌ Deployment encountered an issue, but your website might be available at: ${this.potentialWebsiteUrl}`;
      this.deploymentWebsiteUrl = this.potentialWebsiteUrl;
    } else {
      this.deploymentFinalMessage = `❌ Deployment failed: ${error}`;
      this.deploymentWebsiteUrl = '';
    }
    
    // Show final message immediately - no delay
      this.showDeploymentFinalMessage = true;
    console.log('FormBuilderV2: Showing final error message immediately');
    
    // Force change detection to ensure UI updates
    this.cdr.detectChanges();
  }

  private handleDeploymentTimeout(): void {
    // Clear the publish button loading state
    this.isPublishButtonLoading = false;
    
    // this.isDeploying = false;
    this.deploymentStatus = '⏰ Deployment is taking longer than expected. Please check back in a few minutes.';
    this.deploymentProgress = 0;
    
    console.log('FormBuilderV2: Deployment timeout');
    
    // Set final timeout message with potential URL - always show URL if available
    if (this.potentialWebsiteUrl && this.potentialWebsiteUrl.trim() !== '') {
      this.deploymentFinalMessage = `⏰ Deployment is taking longer than expected. Your website might be available at: ${this.potentialWebsiteUrl}`;
      this.deploymentWebsiteUrl = this.potentialWebsiteUrl;
    } else {
      this.deploymentFinalMessage = '⏰ Deployment is taking longer than expected. Please check back in a few minutes.';
      this.deploymentWebsiteUrl = '';
    }
    
    // Show final message immediately - no delay
      this.showDeploymentFinalMessage = true;
    console.log('FormBuilderV2: Showing final timeout message immediately');
    
    // Force change detection to ensure UI updates
    this.cdr.detectChanges();
  }

  /**
   * Close the deployment overlay and reset deployment state
   * Note: URL information is preserved for potential reopening
   */
  closeDeploymentOverlay(): void {
    // this.isDeploying = false;
    this.showDeploymentFinalMessage = false;
    this.deploymentFinalMessage = '';
    this.deploymentStatus = '';
    this.deploymentProgress = 0;
    this.deploymentAttempts = 0;
    // Preserve potential website URL for future reference
    // this.potentialWebsiteUrl = ''; // Keep this for potential reopening
    this.deploymentWebsiteUrl = '';
    this.deploymentIconStatus = 'loading';
  }

  /**
   * Check if deployment has completed and show final state if needed
   * This ensures URL is always shown even if popup was closed early
   */
  private checkDeploymentCompletion(): void {
    // If we have a potential URL but no final message shown, show it
    if (this.potentialWebsiteUrl && this.potentialWebsiteUrl.trim() !== '' && !this.showDeploymentFinalMessage) {
      this.deploymentWebsiteUrl = this.potentialWebsiteUrl;
      this.deploymentFinalMessage = `🎉 Your website is now live! Visit: ${this.potentialWebsiteUrl}`;
      this.deploymentIconStatus = 'success';
      this.showDeploymentFinalMessage = true;
      console.log('FormBuilderV2: Showing deployment completion with URL after early closure');
    }
  }

  private sendDataToDynamo(): void {
    if (this.companyGuid) {
      this.indexedDBService.prepareDataForDynamo(this.companyGuid)
        .then(data => {
          if (data) {
            const payload = {
              companyId: this.companyGuid,
              capabilities: data.capabilities,
              equipments: data.equipmentTitles
            };

            console.log('FormBuilderV2: Sending payload to DynamoDB:', payload);

            this.http.post(`${environment.dynamoApiBaseUrl}/supplier/UpdateSupplierMetaData`, payload)
              .subscribe({
                next: (response) => {
                  console.log('FormBuilderV2: Successfully sent data to DynamoDB:', response);
                },
                error: (error) => {
                  console.error('FormBuilderV2: Error sending data to DynamoDB:', error);
                }
              });
          } else {
            console.log('FormBuilderV2: No data found for DynamoDB');
          }
        })
        .catch(error => {
          console.error('FormBuilderV2: Error preparing data for DynamoDB:', error);
        });
    }
  }

  /**
   * Handle warning popup close
   */
  onWarningPopupClose(): void {
    this.showWarningPopup = false;
    // Optionally refresh the page or navigate somewhere
    window.location.reload();
  }

  /**
   * Handle image warning overlay close
   */
  onImageWarningOverlayClose(): void {
    this.showImageWarningOverlay = false;
  }

  /**
   * Show About Us tutorial popup if not already completed
   */
  private showAboutUsTutorial(): void {
    console.log('showAboutUsTutorial called, companyGuid:', this.companyGuid);
    
    if (!this.companyGuid) {
      console.warn('Cannot show tutorial: company GUID not available');
      return;
    }

    // Check if tutorial has already been completed
    if (this.tutorialConfigService.isTutorialCompleted(this.companyGuid, 'aboutUs')) {
      console.log('About Us tutorial already completed, skipping');
      return;
    }

    // Get tutorial configuration and show popup
    this.tutorialConfig = this.tutorialConfigService.getAboutUsTutorialConfig(this.companyGuid);
    this.showTutorialPopup = true;
    
    console.log('Showing About Us tutorial popup, config:', this.tutorialConfig);
    console.log('showTutorialPopup set to:', this.showTutorialPopup);
  }

  /**
   * Handle tutorial completion
   */
  onTutorialCompleted(): void {
    console.log('Tutorial completed successfully');
    this.showTutorialPopup = false;
    this.tutorialConfig = null;
  }

  /**
   * Handle tutorial close (user closed without completing)
   */
  onTutorialClosed(): void {
    console.log('Tutorial closed by user');
    this.showTutorialPopup = false;
    this.tutorialConfig = null;
  }

  /**
   * Manually trigger tutorial popup (for testing purposes)
   * This method can be called from browser console for testing
   */
  triggerTutorialForTesting(): void {
    console.log('Manually triggering tutorial for testing');
    this.showAboutUsTutorial();
  }

  /**
   * Show Capabilities tutorial popup if not already completed
   */
  private showCapabilitiesTutorial(): void {
    console.log('showCapabilitiesTutorial called, companyGuid:', this.companyGuid);
    
    if (!this.companyGuid) {
      console.warn('Cannot show capabilities tutorial: company GUID not available');
      return;
    }

    // Check if tutorial has already been completed
    if (this.tutorialConfigService.isTutorialCompleted(this.companyGuid, 'capabilities')) {
      console.log('Capabilities tutorial already completed, skipping');
      return;
    }

    // Get tutorial configuration and show popup
    this.tutorialConfig = this.tutorialConfigService.getCapabilitiesTutorialConfig(this.companyGuid);
    this.showTutorialPopup = true;
    
    console.log('Showing Capabilities tutorial popup, config:', this.tutorialConfig);
    console.log('showTutorialPopup set to:', this.showTutorialPopup);
  }

  /**
   * Update website progress when a section is completed
   */
  private updateWebsiteProgress(section: string, websiteUrl?: string): void {
    if (!this.companyGuid) {
      console.warn('FormBuilderV2: Cannot update progress - companyGuid not available');
      return;
    }

    try {
      const completionStates = {
        isThemeCompleted: this.isThemeCompleted,
        isAboutCompleted: this.isAboutCompleted,
        isCapabilitiesCompleted: this.isCapabilitiesCompleted,
        isContactCompleted: this.isContactCompleted,
        isLogoCompleted: this.isLogoCompleted
      };

      // Calculate completion percentage
      const completionPercentage = this.websiteProgressService.calculateCompletionPercentage(completionStates);

      // Update the progress bar
      this.completionPercentage = completionPercentage;

      // Save completion percentage to IndexedDB via FormBuilderV2Service
      this.formBuilderV2Service.updateCompletionPercentage(completionPercentage);

      const updateWebsiteProgressServiceCall = this.websiteProgressService.updateWebsiteProgress(
        this.companyGuid, section, completionPercentage);
      // Send progress update to API
      updateWebsiteProgressServiceCall.subscribe({
        next: (response) => {
          console.log('FormBuilderV2: Successfully updated website progress:', response);
        },
        error: (error) => {
          console.error('FormBuilderV2: Error updating website progress:', error);
          // Don't throw error to prevent breaking the user flow
        }
      });
    } catch (error) {
      console.error('FormBuilderV2: Error in updateWebsiteProgress:', error);
      // Don't throw error to prevent breaking the user flow
    }
  }

  private async updateSupplierData(): Promise<void> {
    if (!this.companyGuid) {
      console.error('Cannot update supplier data: company GUID is missing');
      return;
    }
    try {
      const result = await this.supplierSiteDataService.saveSupplierSiteData(this.companyGuid);
      this.refreshImageTagsCache();
  
      if (result.success) {
        console.log('✅ Supplier data updated successfully');
      } else {
        console.error('❌ Supplier data update failed:', result.message);
      }
    } catch (error) {
      console.error('❌ Supplier data update error:', error);
    } finally {
      this.cdr.detectChanges();
    }
  }

  /**
   * Process files for deployment by replicating content-manager functionality
   * This method updates the files in WebsiteFileService for deployment
   */
  private async processFilesForDeployment(): Promise<void> {
    try {
      console.log('🔄 Processing files for deployment...');
      
      // 1. Get current memory files
      const memoryFiles = this.websiteFileService.getAllFiles();
      console.log('📁 Retrieved memory files:', memoryFiles.size);
      
      if (!memoryFiles || memoryFiles.size === 0) {
        throw new Error('No memory files found. Please ensure the template is loaded.');
      }
      
      // 2. Get current content data from IndexedDB
      const currentContent = await this.indexedDBService.getDictContent(this.companyGuid || '');
      console.log('📄 Retrieved content data:', currentContent);
      
      if (!currentContent) {
        throw new Error('No content data found. Please ensure the site has been generated.');
      }
      
      // 3. Detect current template
      const currentTemplate = this.detectCurrentTemplate(memoryFiles);
      console.log(`🎨 Detected template: ${currentTemplate}`);
      
      // 4. Process HTML files with template-specific logic
      for (const [filePath, fileData] of memoryFiles) {
        if (filePath.endsWith('.html') && typeof fileData.content === 'string') {
          console.log(`🔄 Processing HTML file: ${filePath}`);
          const processedHtml = await this.processHtmlFileWithTemplate(fileData.content, currentContent, currentTemplate);
          
          // Update the file in WebsiteFileService
          this.websiteFileService.updateFileContent(filePath, processedHtml);
        }
      }
      
      // 5. Remove unnecessary JS files from memory
      this.websiteFileService.updateFileContent('js/content-manager.js', '');
      this.websiteFileService.updateFileContent('js/dict.js', '');
      console.log('🗑️ Removed unnecessary JS files from deployment');
      
      console.log('✅ Files processed for deployment successfully');
      
    } catch (error) {
      console.error('❌ Error processing files for deployment:', error);
      throw error;
    }
  }


  /**
   * Detect current template from memory files
   */
  private detectCurrentTemplate(memoryFiles: Map<string, any>): number {
    // Check for template-specific files or content
    for (const [filePath, fileData] of memoryFiles) {
      if (filePath.includes('supplier-template-')) {
        const match = filePath.match(/supplier-template-(\d+)/);
        if (match) {
          return parseInt(match[1]);
        }
      }
    }
    
    // Fallback: check the current template from WebsiteFileService
    return (this.websiteFileService as any).currentTemplate || 1;
  }

  /**
   * Template-aware HTML processing
   */
  private async processHtmlFileWithTemplate(htmlContent: string, contentData: any, templateNumber: number): Promise<string> {
    // Parse HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    
    // 1. Replicate Service Cards (template-aware)
    await this.replicateServiceCardsWithTemplate(doc, contentData, templateNumber);
    
    // 2. Replicate Equipment Cards (template-aware)
    await this.replicateEquipmentCardsWithTemplate(doc, contentData, templateNumber);
    
    // 3. Replicate Additional Service Cards (template-aware)
    await this.replicateAdditionalServiceCardsWithTemplate(doc, contentData, templateNumber);
    
    // 4. Populate ALL static content (same for all templates)
    this.populateStaticContentInHtml(doc, contentData);
    
    // 5. Remove unnecessary scripts
    this.removeUnnecessaryScripts(doc);
    
    return doc.documentElement.outerHTML;
  }

  /**
   * Process individual HTML file to replicate content-manager functionality (legacy method)
   */
  private async processHtmlFile(htmlContent: string, contentData: any): Promise<string> {
    // Parse HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    
    // 1. Replicate Service Cards
    await this.replicateServiceCards(doc, contentData);
    
    // 2. Replicate Equipment Cards
    await this.replicateEquipmentCards(doc, contentData);
    
    // 3. Replicate Additional Service Cards
    await this.replicateAdditionalServiceCards(doc, contentData);
    
    // 4. 🆕 Populate ALL static content (replicates populateStaticContent from content-manager.js)
    this.populateStaticContentInHtml(doc, contentData);
    
    // 5. Remove unnecessary scripts
    this.removeUnnecessaryScripts(doc);
    
    return doc.documentElement.outerHTML;
  }

  /**
   * Get template-specific configuration including highlight logic
   */
  private getTemplateConfig(templateNumber: number): any {
    const configs: { [key: number]: any } = {
      1: {
        serviceCardSelector: '.capabilities-grid-item:not(.template-card)',
        equipmentCardSelector: '.equipment-grid-item:not(.template-card)',
        additionalServiceCardSelector: '.capabilities-grid-item:not(.template-card)',
        hasServiceHighlight: false,
        serviceHighlightLogic: null
      },
      2: {
        serviceCardSelector: '.capability-box:not(.template-card)',
        equipmentCardSelector: '.equipment-item:not(.template-card)',
        additionalServiceCardSelector: '.capability-box:not(.template-card)',
        hasServiceHighlight: false,
        serviceHighlightLogic: null
      },
      3: {
        serviceCardSelector: '.capability-card:not(.template-card)',
        equipmentCardSelector: '.equipment-item:not(.template-card)',
        additionalServiceCardSelector: '.capability-card:not(.template-card)',
        hasServiceHighlight: true,
        serviceHighlightLogic: (index: number) => index % 2 !== 0 // Every odd card
      },
      4: {
        serviceCardSelector: '.capability-item:not(.template-card)',
        equipmentCardSelector: '.equipment-item:not(.template-card)',
        additionalServiceCardSelector: '.capability-item:not(.template-card)',
        hasServiceHighlight: true,
        serviceHighlightLogic: (index: number) => (index - 1) % 4 < 2 && index > 0 // Complex pattern
      },
      5: {
        serviceCardSelector: '.service-card:not(.template-card)',
        equipmentCardSelector: '.equipment-card:not(.template-card)',
        additionalServiceCardSelector: '.service-card:not(.template-card)',
        hasServiceHighlight: false,
        serviceHighlightLogic: null
      }
    };
    
    return configs[templateNumber] || configs[1]; // Default to template 1
  }

  /**
   * Template-aware service cards replication with highlight logic
   */
  private async replicateServiceCardsWithTemplate(doc: Document, contentData: any, templateNumber: number): Promise<void> {
    const container = doc.querySelector('#services-container');
    if (!container) return;
    
    const services = contentData.CapabilitiesSection?.services || [];
    const template = container.querySelector('.template-card');
    
    if (!template) return;
    
    // Template-specific selectors and highlight logic
    const templateConfig = this.getTemplateConfig(templateNumber);
    console.log(`🔧 Replicating ${services.length} service cards for template ${templateNumber}`);
    
    // Clear existing cards (template-specific selector)
    const existingCards = container.querySelectorAll(templateConfig.serviceCardSelector);
    existingCards.forEach(card => card.remove());
    
    // Create new cards
    services.forEach((service: any, index: number) => {
      const clone = template.cloneNode(true) as HTMLElement;
      clone.classList.remove('template-card');
      clone.style.display = 'block';
      
      const titleElement = clone.querySelector('.service-title');
      const descriptionElement = clone.querySelector('.service-description');
      
      if (titleElement) {
        const tagName = `SERVICE_${index + 1}_TITLE`;
        titleElement.textContent = service[tagName] || '';
        titleElement.setAttribute('tag', tagName);
      }
      
      if (descriptionElement) {
        const tagName = `SERVICE_${index + 1}_DESCRIPTION`;
        descriptionElement.textContent = service[tagName] || '';
        descriptionElement.setAttribute('tag', tagName);
      }
      
      // 🆕 Apply highlight logic if template supports it
      if (templateConfig.hasServiceHighlight && templateConfig.serviceHighlightLogic) {
        if (templateConfig.serviceHighlightLogic(index)) {
          clone.classList.add('highlighted');
          console.log(`✨ Added highlight to service card ${index + 1}`);
        }
      }
      
      container.appendChild(clone);
    });
    
    // Remove template
    template.remove();
  }

  /**
   * Template-aware equipment cards replication (no highlight logic for equipment)
   */
  private async replicateEquipmentCardsWithTemplate(doc: Document, contentData: any, templateNumber: number): Promise<void> {
    const container = doc.querySelector('#equipment-container');
    if (!container) return;
    
    const equipment = contentData.EquipmentSection?.equipmentItems || [];
    const template = container.querySelector('.template-card');
    
    if (!template) return;
    
    // Template-specific selectors
    const templateConfig = this.getTemplateConfig(templateNumber);
    console.log(`🔧 Replicating ${equipment.length} equipment cards for template ${templateNumber}`);
    
    // Clear existing cards (template-specific selector)
    const existingCards = container.querySelectorAll(templateConfig.equipmentCardSelector);
    existingCards.forEach(card => card.remove());
    
    // Create new cards
    equipment.forEach((item: any, index: number) => {
      const clone = template.cloneNode(true) as HTMLElement;
      clone.classList.remove('template-card');
      clone.style.display = 'block';
      
      const titleElement = clone.querySelector('.equipment-title');
      const descriptionElement = clone.querySelector('.equipment-description');
      
      if (titleElement) {
        const tagName = `EQUIPMENT_${index + 1}_TITLE`;
        titleElement.textContent = item[tagName] || '';
        titleElement.setAttribute('tag', tagName);
      }
      
      if (descriptionElement) {
        const tagName = `EQUIPMENT_${index + 1}_DESCRIPTION`;
        descriptionElement.textContent = item[tagName] || '';
        descriptionElement.setAttribute('tag', tagName);
      }
      
      // Note: No highlight logic for equipment cards in any template
      
      container.appendChild(clone);
    });
    
    // Remove template
    template.remove();
  }

  /**
   * Template-aware additional service cards replication (no highlight logic for additional services)
   */
  private async replicateAdditionalServiceCardsWithTemplate(doc: Document, contentData: any, templateNumber: number): Promise<void> {
    const container = doc.querySelector('#additional-services-container');
    if (!container) return;
    
    const additionalServices = contentData.AdditionalServicesSection?.additionalServices || [];
    const template = container.querySelector('.template-card');
    
    if (!template) return;
    
    // Template-specific selectors
    const templateConfig = this.getTemplateConfig(templateNumber);
    console.log(`🔧 Replicating ${additionalServices.length} additional service cards for template ${templateNumber}`);
    
    // Clear existing cards (template-specific selector)
    const existingCards = container.querySelectorAll(templateConfig.additionalServiceCardSelector);
    existingCards.forEach(card => card.remove());
    
    // Create new cards
    additionalServices.forEach((service: any, index: number) => {
      const clone = template.cloneNode(true) as HTMLElement;
      clone.classList.remove('template-card');
      clone.style.display = 'block';
      
      const titleElement = clone.querySelector('.additional-service-title');
      const descriptionElement = clone.querySelector('.additional-service-description');
      
      if (titleElement) {
        const tagName = `ADDITIONAL_SERVICE_${index + 1}_TITLE`;
        titleElement.textContent = service[tagName] || '';
        titleElement.setAttribute('tag', tagName);
      }
      
      if (descriptionElement) {
        const tagName = `ADDITIONAL_SERVICE_${index + 1}_DESCRIPTION`;
        descriptionElement.textContent = service[tagName] || '';
        descriptionElement.setAttribute('tag', tagName);
      }
      
      // Note: No highlight logic for additional service cards in any template
      
      container.appendChild(clone);
    });
    
    // Remove template
    template.remove();
  }

  /**
   * Replicate service cards functionality from content-manager.js (legacy method)
   */
  private async replicateServiceCards(doc: Document, contentData: any): Promise<void> {
    const container = doc.querySelector('#services-container');
    if (!container) return;
    
    const services = contentData.CapabilitiesSection?.services || [];
    const template = container.querySelector('.template-card');
    
    if (!template) return;
    
    console.log(`🔧 Replicating ${services.length} service cards`);
    
    // Clear existing cards (except template)
    const existingCards = container.querySelectorAll('.capabilities-grid-item:not(.template-card)');
    existingCards.forEach(card => card.remove());
    
    // Create new cards
    services.forEach((service: any, index: number) => {
      const clone = template.cloneNode(true) as HTMLElement;
      clone.classList.remove('template-card');
      clone.style.display = 'block';
      
      const titleElement = clone.querySelector('.service-title');
      const descriptionElement = clone.querySelector('.service-description');
      
      if (titleElement) {
        const tagName = `SERVICE_${index + 1}_TITLE`;
        titleElement.textContent = service[tagName] || '';
        titleElement.setAttribute('tag', tagName);
      }
      
      if (descriptionElement) {
        const tagName = `SERVICE_${index + 1}_DESCRIPTION`;
        descriptionElement.textContent = service[tagName] || '';
        descriptionElement.setAttribute('tag', tagName);
      }
      
      container.appendChild(clone);
    });
    
    // Remove template
    template.remove();
  }

  /**
   * Replicate equipment cards functionality from content-manager.js
   */
  private async replicateEquipmentCards(doc: Document, contentData: any): Promise<void> {
    const container = doc.querySelector('#equipment-container');
    if (!container) return;
    
    const equipment = contentData.EquipmentSection?.equipmentItems || [];
    const template = container.querySelector('.template-card');
    
    if (!template) return;
    
    console.log(`🔧 Replicating ${equipment.length} equipment cards`);
    
    // Clear existing cards
    const existingCards = container.querySelectorAll('.equipment-item:not(.template-card)');
    existingCards.forEach(card => card.remove());
    
    // Create new cards
    equipment.forEach((item: any, index: number) => {
      const clone = template.cloneNode(true) as HTMLElement;
      clone.classList.remove('template-card');
      clone.style.display = 'block';
      
      const titleElement = clone.querySelector('.equipment-title');
      const descriptionElement = clone.querySelector('.equipment-description');
      
      if (titleElement) {
        const tagName = `EQUIPMENT_${index + 1}_TITLE`;
        titleElement.textContent = item[tagName] || '';
        titleElement.setAttribute('tag', tagName);
      }
      
      if (descriptionElement) {
        const tagName = `EQUIPMENT_${index + 1}_DESCRIPTION`;
        descriptionElement.textContent = item[tagName] || '';
        descriptionElement.setAttribute('tag', tagName);
      }
      
      container.appendChild(clone);
    });
    
    // Remove template
    template.remove();
  }

  /**
   * Replicate additional service cards functionality from content-manager.js
   */
  private async replicateAdditionalServiceCards(doc: Document, contentData: any): Promise<void> {
    const container = doc.querySelector('#additional-services-container');
    if (!container) return;
    
    const additionalServices = contentData.AdditionalServicesSection?.additionalServices || [];
    const template = container.querySelector('.template-card');
    
    if (!template) return;
    
    console.log(`🔧 Replicating ${additionalServices.length} additional service cards`);
    
    // Clear existing cards
    const existingCards = container.querySelectorAll('.capabilities-grid-item:not(.template-card)');
    existingCards.forEach(card => card.remove());
    
    // Create new cards
    additionalServices.forEach((service: any, index: number) => {
      const clone = template.cloneNode(true) as HTMLElement;
      clone.classList.remove('template-card');
      clone.style.display = 'block';
      
      const titleElement = clone.querySelector('.additional-service-title');
      const descriptionElement = clone.querySelector('.additional-service-description');
      
      if (titleElement) {
        const tagName = `ADDITIONAL_SERVICE_${index + 1}_TITLE`;
        titleElement.textContent = service[tagName] || '';
        titleElement.setAttribute('tag', tagName);
      }
      
      if (descriptionElement) {
        const tagName = `ADDITIONAL_SERVICE_${index + 1}_DESCRIPTION`;
        descriptionElement.textContent = service[tagName] || '';
        descriptionElement.setAttribute('tag', tagName);
      }
      
      container.appendChild(clone);
    });
    
    // Remove template
    template.remove();
  }

  /**
   * Populate all static content elements - replicates populateStaticContent() from content-manager.js
   * This handles ALL static content sections, not just company info
   */
  private populateStaticContentInHtml(doc: Document, contentData: any): void {
    console.log('🏷️ Populating static content...');
    
    const contentToUse = contentData;
    
    // Company Information
    this.populateElementWithTagInHtml(doc, 'COMPANY_NAME', contentToUse.CompanyInfo?.COMPANY_NAME);
    this.populateElementWithTagInHtml(doc, 'COMPANY_EMAIL', contentToUse.CompanyInfo?.COMPANY_EMAIL);
    this.populateElementWithTagInHtml(doc, 'COMPANY_PHONE', contentToUse.CompanyInfo?.COMPANY_PHONE);
    this.populateElementWithTagInHtml(doc, 'COMPANY_ADDRESS', contentToUse.CompanyInfo?.COMPANY_ADDRESS);
    this.populateElementWithTagInHtml(doc, 'COMPANY_COPYRIGHT', contentToUse.CompanyInfo?.COMPANY_COPYRIGHT);
    
    // Hero Section
    this.populateElementWithTagInHtml(doc, 'HERO_MAIN_TITLE', contentToUse.HeroSection?.HERO_MAIN_TITLE);
    this.populateElementWithTagInHtml(doc, 'HERO_SUBTITLE', contentToUse.HeroSection?.HERO_SUBTITLE);
    this.populateElementWithTagInHtml(doc, 'HERO_DESCRIPTION', contentToUse.HeroSection?.HERO_DESCRIPTION);
    
    // About Section
    if (contentToUse.AboutSection) {
      this.populateElementWithTagInHtml(doc, 'ABOUT_MAIN_HEADING', contentToUse.AboutSection.ABOUT_MAIN_HEADING);
      this.populateElementWithTagInHtml(doc, 'ABOUT_DESCRIPTION_1', contentToUse.AboutSection.ABOUT_DESCRIPTION_1);
      this.populateElementWithTagInHtml(doc, 'ABOUT_DESCRIPTION_2', contentToUse.AboutSection.ABOUT_DESCRIPTION_2);
    }
    
    // Capabilities Section
    this.populateElementWithTagInHtml(doc, 'CAPABILITIES_MAIN_HEADING', contentToUse.CapabilitiesSection?.CAPABILITIES_MAIN_HEADING);
    this.populateElementWithTagInHtml(doc, 'CAPABILITIES_DESCRIPTION', contentToUse.CapabilitiesSection?.CAPABILITIES_DESCRIPTION);
    
    // Equipment Section
    this.populateElementWithTagInHtml(doc, 'EQUIPMENT_OVERVIEW_DESCRIPTION', contentToUse.EquipmentSection?.EQUIPMENT_OVERVIEW_DESCRIPTION);
    
    // Additional Services Section
    this.populateElementWithTagInHtml(doc, 'ADDITIONAL_SERVICES_HEADING', contentToUse.AdditionalServicesSection?.ADDITIONAL_SERVICES_HEADING);
    this.populateElementWithTagInHtml(doc, 'ADDITIONAL_SERVICES_DESCRIPTION', contentToUse.AdditionalServicesSection?.ADDITIONAL_SERVICES_DESCRIPTION);
    
    // Contact Section
    this.populateElementWithTagInHtml(doc, 'CONTACT_MAIN_HEADING', contentToUse.ContactSection?.CONTACT_MAIN_HEADING);
    this.populateElementWithTagInHtml(doc, 'CONTACT_DESCRIPTION', contentToUse.ContactSection?.CONTACT_DESCRIPTION);
    
    // Footer Section
    this.populateElementWithTagInHtml(doc, 'FOOTER_DESCRIPTION', contentToUse.FooterSection?.FOOTER_DESCRIPTION);
    
    console.log('✅ Static content populated successfully');
  }

  /**
   * Helper function to populate an element with a specific tag - replicates populateElementWithTag() from content-manager.js
   */
  private populateElementWithTagInHtml(doc: Document, tagName: string, defaultValue: any): void {
    const elements = doc.querySelectorAll(`[tag="${tagName}"]`);
    
    if (elements.length > 0) {
      elements.forEach(element => {
        // Only populate if the element is empty or contains placeholder text
        const currentText = element.textContent?.trim() || '';
        if (this.shouldPopulateElement(currentText)) {
          element.textContent = defaultValue || '';
          console.log(`✅ Populated ${tagName} with: "${defaultValue}"`);
        } else {
          console.log(`⏭️ Skipped ${tagName} (already has content): "${currentText}"`);
        }
      });
    } else {
      console.log(`⚠️ No elements found with tag: ${tagName}`);
    }
  }

  /**
   * Check if an element should be populated - replicates the exact logic from content-manager.js
   */
  private shouldPopulateElement(currentText: string): boolean {
    const placeholderTexts = [
      '',
      'Lorem ipsum dolor sit amet',
      'Lorem ipsum dolor sit amet dolor',
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Morbi auctor rutrum rutrum.',
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
      '(555) 123-4567',
      'info@manufacturingcompany.com',
      '123 Manufacturing Drive, Industrial Park, CA 90210',
      '© 2025 {{CompanyName}}. All Rights Reserved.',
      '© 2026 {{CompanyName}}. All Rights Reserved.',
      'Home - Website Template'
    ];
    
    return placeholderTexts.includes(currentText);
  }

  /**
   * Populate all content based on tag attributes (legacy method - kept for compatibility)
   */
  private populateContentByTags(doc: Document, contentData: any): void {
    // Get all elements with tag attributes
    const taggedElements = doc.querySelectorAll('[tag]');
    console.log(`🏷️ Populating ${taggedElements.length} tagged elements`);
    
    taggedElements.forEach(element => {
      const tagName = element.getAttribute('tag');
      if (!tagName) return;
      
      const value = this.getNestedProperty(contentData, tagName);
      if (value !== undefined && value !== null) {
        element.textContent = value;
      }
    });
  }

  /**
   * Get nested property value from object using dot notation
   */
  private getNestedProperty(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : undefined;
    }, obj);
  }

  /**
   * Remove unnecessary scripts from the document
   */
  private removeUnnecessaryScripts(doc: Document): void {
    // Remove content-manager.js and dict.js script tags
    const scripts = doc.querySelectorAll('script[src]');
    let removedCount = 0;
    
    scripts.forEach(script => {
      const src = script.getAttribute('src');
      if (src && (src.includes('content-manager.js') || src.includes('dict.js'))) {
        script.remove();
        removedCount++;
      }
    });
    
    console.log(`🗑️ Removed ${removedCount} unnecessary script tags`);
  }

} 
 